import {
  gSupportEmail,
  kEnterpriseConnectorList,
  kAppendConnectorList,
  kMergeConnectorList
} from "./whitelabel/brand";

import { escapeRegExp } from "./utils/escapeRegExp";
import { LocationSource } from "./CoreApp/LocationSource";

export const supportEmailaddr = gSupportEmail;
export const kVersionStr = "2.1.73 2026-10-28";

export const enumMFAState = {
  notConfigured: 0,
  requiredInvalidEmail: 1,
  required: 2,
  enabledAuth: 3,
  overrideMFA: 4
};
export var enumFolderStatus = { notingroup: 0, ingroup: 1, ingrouplocked: 2 };
export const kAccountActive = "Active";
export const kAccountDisabled = "Disabled";
export const kAccountTrial = "Free Trial";
export const kAccountTrialExpired = "Expired Trial";
export const kAccountExpired = "Expired";
export const kAccountCustomer = "Customer";
export const kAccountInternal = "Internal";
export const kAccountPartner = "Partner";
export const kAccountPilot = "Pilot";
export const kAccountOther = "Other";
export const kAccountUnknown = "Unknown";
export const kAccountTeam = "Team";
export const kAccountPremium = "Premium";
export const kAccountEnterprise = "Enterprise";
export const kAccountCore = "Core";
export const kAccountPlus = "Plus";
export const kAccountPro = "Pro";
export const kAccountADPBasic = "ADP Basic";
export const kAccountADPPremium = "ADP Premium";
export const kAccountSandbox = "Customer-Sandbox";
export const kEditionLabels = {
  isolved_tier_0: "Org Chart",
  isolved_tier_1: "Enhanced Org Chart",
  isolved_tier_2: "Org Chart & Position Planning"
};

// Brand identifiers for whitelabel filtering
export const enumBrand = {
  default: "",
  isolved: "isolved",
  SC: "SC",
  PS: "PS"
};
export const kIsolvedBoxStyleIDs = new Set([340, 341, 342, 343, 344, 345, 346]);
export const kROUser = "ROUser";
export const kRWUser = "RWUser";
export const kAdminUser = "Admin";
export const enumUIModes = {
  edit: 0,
  chart: 1,
  directory: 2,
  planning: 3,
  background: 5,
  boxlayouteditor: 6,
  settings: 7,
  details: 8,
  reports: 9
};
export const appRoutes = {
  edit: "/edit",
  chart: "/chart",
  directory: "/directory",
  planning: "/planning",
  boxlayout: "/boxlayouteditor",
  settings: "/settings",
  details: "/details",
  reports: "/reports"
};
export const defaultRouteMode = {
  base: enumUIModes.chart,
  open: enumUIModes.chart,
  plan: enumUIModes.planning,
  boxlayout: enumUIModes.boxlayouteditor
};

export type RouteSegment = keyof typeof defaultRouteMode;
export type UIModeName = keyof typeof enumUIModes;

/** Valid ?mode= values per resource route segment. Invalid combos fall back to the route default. */
export const validModesPerRoute: Record<RouteSegment, UIModeName[]> = {
  base: ["chart", "directory", "reports"],
  open: ["chart", "edit", "directory", "reports"],
  plan: ["chart", "planning", "directory", "reports"],
  boxlayout: ["boxlayouteditor"]
};

export function isValidModeForRoute(
  segment: string,
  mode: string,
  params: URLSearchParams
): boolean {
  if (
    (segment as RouteSegment) === "base" &&
    (mode as UIModeName) === "edit" &&
    params.get("create") === "true"
  ) {
    return true;
  }
  const allowed = validModesPerRoute[segment as RouteSegment];
  return allowed ? allowed.includes(mode as UIModeName) : true;
}

/** ?create=true is only meaningful on /base/ (chart-builder launcher / create-from-base). */
export function isValidCreateParam(segment: string): boolean {
  return (segment as RouteSegment) === "base";
}

/**
 * Runtime counterpart to isValidModeForRoute. Some structurally-valid URL
 * modes additionally require backing session state set by the initiating
 * in-session action. Pure function — caller passes the runtime context
 * (typically Globals._wsMgr) so tests don't need global mocks.
 *
 * Returns true when the mode is backed OR when the combo has no runtime
 * requirement. Returns false only when a runtime requirement exists and
 * the context doesn't satisfy it.
 */
export function isModeBackedByRuntime(
  segment: string,
  mode: string,
  params: URLSearchParams,
  runtime:
    | {
        fBuilderInfo?: unknown;
        GetDoc?: () => { IsCustomChart?: () => boolean } | null | undefined;
      }
    | null
    | undefined
): boolean {
  // ?mode=edit&create=true is the in-session create/PIT flow. Universal
  // question — applies regardless of segment, since the same passenger
  // pair rides on /boxlayout/, /settings/, and /details/ during sub-route
  // round-trips. Two lifetime windows:
  //   1. Pre-load: the initiating nav sets wsMgr.fBuilderInfo synchronously
  //      before navigating. Valid until handleBuilderChart consumes it.
  //   2. Post-load: fBuilderInfo is nulled once metadata transfers, but the
  //      loaded doc is now an unsaved custom chart (IsCustomChart === true)
  //      for the entire unsaved-edit lifetime until save/abandon.
  // Cold-launch/pasted URLs satisfy neither — no initiating state ran, and
  // the doc that loaded is a server chart (given FinishLogin now falls back
  // to the default chart on non-canvas resource routes like /boxlayout/).
  if ((mode as UIModeName) === "edit" && params.get("create") === "true") {
    return !!runtime?.fBuilderInfo || !!runtime?.GetDoc?.()?.IsCustomChart?.();
  }
  return true;
}

/** Write-side counterpart to isModeBackedByRuntime's edit+create=true check:
 *  does entering edit mode on this segment need ?create=true appended? */
export function needsCreateParamForEdit(
  segment: string,
  runtime:
    { GetDoc?: () => { IsCustomChart?: () => boolean } | null | undefined } | null | undefined
): boolean {
  return isValidCreateParam(segment) && !!runtime?.GetDoc?.()?.IsCustomChart?.();
}

export const APP_PREFIX = "/app";
const resourcePrefixes = ["/base/", "/open/", "/plan/"];
export const appCanvasRoutes = ["/", "/edit", "/chart", "/planning", "/boxlayouteditor"];
const canvasPrefixes = ["/base/", "/open/", "/plan/", "/boxlayout/"];

/**
 * Normalize a pathname by stripping the standalone /app basename prefix. Accepts both
 * basename-stripped paths (React Router with basename="/app") and raw window.location.pathname.
 * Bare "/app" maps to "/" so canvas-root navigation resolves correctly.
 */
function stripAppPrefix(pathname: string): string {
  if (pathname === APP_PREFIX) return "/";
  if (pathname.startsWith(APP_PREFIX + "/")) return pathname.slice(APP_PREFIX.length);
  return pathname;
}

/**
 * True for the three identity-bearing resource URLs only: /base/{id} (BASE CHART),
 * /open/{dir}/{file} (CUSTOM CHART), /plan/{id} (PLAN). Used by updateUrlToResourceRoute
 * to decide whether coreapp may sync the URL to the loaded doc's identity. Stricter than
 * isCanvasRoute (below), which also includes /boxlayout/ and legacy mode routes.
 */
export function isResourceRoute(pathname = LocationSource.getPathname()) {
  const p = stripAppPrefix(pathname);
  return resourcePrefixes.some(prefix => p.startsWith(prefix));
}

/**
 * True for any route that mounts the main canvas/editor surface: resource routes (/base/,
 * /open/, /plan/), box-layout editor, and legacy mode routes (/, /edit, /chart, /planning,
 * /boxlayouteditor). Used for mode-transition gating and canvas-context-param handling.
 * Broader than isResourceRoute (above), which only matches the three identity-bearing
 * resource URLs.
 */
export function isCanvasRoute(pathname: string): boolean {
  const p = stripAppPrefix(pathname);
  return (
    canvasPrefixes.some(prefix => p.startsWith(prefix)) ||
    appCanvasRoutes.some(r => p === r || p.startsWith(r + "/"))
  );
}

export const legacyToRouteParamMap: Record<string, string> = {
  chartID: "chartID",
  file: "file",
  directory: "directory",
  launchPlanID: "planID",
  mode: "mode",
  tool: "tool",
  view: "view",
  topboxid: "topOfChart",
  division: "division",
  levels: "levels"
};

export const enumUISubModes = {
  standard: null,
  backgroundeditor: 1,
  viewmanager: 2
};
export const enumChartOpenModes = { newdoc: 0, opendoc: 1, masterchart: 2 };
export const enumPaywallModes = { none: 0, limited: 1, full: 2, existing: 3 };
export const kDefaultEmployeeHeadcountView = "Headcount Template";
export const kDefaultEmployeeDepartmentView = "Department Template";
export const kDefaultEmployeePhotoView = "Photo Template";
export const kDefaultPositionBasicView = "Position-Basic";
export const kDefaultPositionBudgetView = "Budget";
export const kDefaultPositionSpanOfControlView = "Span of control";
export const kDefaultPositionStaffingView = "Staffing";
export const kDefaultEmployeeViews = [
  kDefaultEmployeeHeadcountView,
  kDefaultEmployeeDepartmentView,
  kDefaultEmployeePhotoView
];
export const kDefaultPositionViews = [
  kDefaultPositionBasicView,
  kDefaultPositionBudgetView,
  kDefaultPositionSpanOfControlView,
  kDefaultPositionStaffingView
];
export const kAlertButtonRadius = 4;
export var kAlertWidth = 400;
export const kAlertHeight = 160;
export const kAlertButtonWidth = 64;
export const kAlertButtonHeight = 20;
export const kAlertButtonSpacing = 10;
export const kDefaultPromptWidth = 500;
export const kDefaultPromptHeight = 500;

export const kCanvasRightClickWidth = 200;
export var kArrangeOptions = [
  "alignleft",
  "aligncenter",
  "alignright",
  "aligntop",
  "alignmiddle",
  "alignbottom",
  "bringtofront",
  "sendtoback",
  "bringforward",
  "sendbackward"
];
export var kFArrangeHover = [
  "alignobjleft",
  "alignobjcenter",
  "alignobjright",
  "alignobjtop",
  "alignobjmiddle",
  "alignobjbottom",
  "bringfront",
  "sendtoback",
  "bringforward",
  "sendback"
];
export var kArrangeCmds = [
  "alignleft",
  "aligncenter",
  "alignright",
  "aligntop",
  "alignmiddle",
  "alignbottom",
  "bringtofront",
  "sendtoback",
  "bringforward",
  "sendbackward"
];
export var kArrangeIcons = [
  "alignleft",
  "aligncenter",
  "alignright",
  "aligntop",
  "alignmiddle",
  "alignbottom",
  "bringtofront",
  "sendtoback",
  "bringforward",
  "sendbackward"
];
export var kArrangeRequiresTwoRoots = [
  true,
  true,
  true,
  true,
  true,
  true,
  false,
  false,
  false,
  false
];
export const kGroupingOptions = ["grouptype1", "grouptype2", "grouptype3", "ungroup"];
export const kGroupingHover = ["groupheader", "grouppeer", "groupcomgr", "ungrouppeer"];
export const kGroupingCmds = ["grouptype1", "grouptype2", "grouptype3", "ungrouppeer"];
export const kGroupingIcons = ["groupprops", "grouping", "comgr", "ungroup"];
export var enumMRBList = { mrbOnly: 0, boxesOnly: 1 };
export const enumAutoTextType = {
  none: -1,
  pagenum: 0,
  totalpages: 1,
  pagex: 2,
  pagexofy: 3,
  currentdate: 4,
  subchartname: 5,
  subchartnum: 6,
  subchartpath: 7,
  filename: 8,
  companyname: 9,
  chainofcmd: 10,
  division: 11,
  refreshdate: 12,
  refreshtime: 13
};
export const kAutoTextStrs = [
  "pagenum",
  "totalpages",
  "pagex",
  "pagexy",
  "today",
  "subchartname",
  "subchartnum",
  "subchartpath",
  "filename",
  "companyname",
  "chainofcommand",
  "Division",
  "refreshdate",
  "refreshtime"
];
export const kTitleAutoText = [
  "subchartname",
  "subchartnum",
  "subchartpath",
  "filename",
  "companyname",
  "chainofcommand",
  "Division"
];
export const kDateAutoText = ["today", "refreshdate", "refreshtime"];
export const kPageNumAutoText = ["pagenum", "totalpages", "pagex", "pagexy"];
export const kSpecialCharsList = ["©", "®", "£", "¡", "¿", "™", "📱", "☎"];
export var monthNames = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december"
];
export var abbrmonthNames = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec"
];
export const enumDateTimeDisplayOptions = {
  date: 0,
  time: 1,
  datetime: 3,
  weekdaytime: 4,
  numericdate: 5,
  shortdate: 6,
  shortdatetime: 7,
  todayyesterday: 8
};
export const enumLocaleOptions = {
  en: "en-EN",
  es: "es-ES",
  fr: "fr-FR",
  de: "de-DE"
};
export const kMinPickerWidth = 200;
export const kWSPanelGap = 10;
export const kWSPanelMargin = 10;
export const kMinWorkspaceWidth = 500;
export const enumChartRenderOrder = {
  underlay: 0,
  connectingLine: 1,
  chartBoxGroups: 2,
  chartBox: 3,
  groups: 4,
  textBox: 5,
  overlay: 6
};
export const enumChartElement = {
  gutterCell: 0,
  boxCell: 1,
  connectingLine: 2,
  chartBox: 3,
  textBox: 4,
  scArrow: 5
};
export const enumChartBoxInteractionMode = {
  readOnly: 0,
  content: 1,
  layout: 2,
  marquee: 3,
  boxstyle: 4
};
export var enumWrapTextMode = {
  nowrap: 0,
  oneline: 1,
  twolines: 2,
  threelines: 3,
  fourlines: 4,
  unlimited: 5
};
export var enumPictureType = { notPicture: 0, staticPicture: 1, fieldPicture: 2, iconPicture: 3 };
export const enumCellObjectSubtypes = {
  alsoreportsto: 0,
  enumeratedtable: 1,
  picklist: 2,
  objinfo: 3,
  list: 4,
  icon: 5
};
export const enumEmptyPositionLayoutType = { vacant: 0, closed: 1, open: 2 };
export var enumCellRefType = { topbox: 0, branch: 1 };
export var enumPicMask = { nomask: 0, circle: 1, grayscale: 2 };
export var kMissingPic = "missing.jpg";
export var kDefPicFrameW = 72;
export var kDefPicFrameH = 72;
export const enumCellContentType = {
  Field: 0,
  Blank: 1,
  FreeText: 2,
  AutoText: 3,
  Picture: 4,
  pieChart: 5,
  object: 6,
  tableValue: 7,
  tableSum: 8,
  tablePercent: 9,
  icon: 10
};
export const enumFieldDisplayMode = {
  FieldOnly: 0,
  LabelAndField: 1,
  LabelOnly: 2,
  Hidden: 3
};
export const enumBorderLineType = {
  leftBorder: 0,
  topBorder: 1,
  rightBorder: 2,
  bottomBorder: 3
};
export const enumUpdateProperty = {
  boxFillColor: 0,
  boxBorderColor: 1,
  boxShapeType: 2,
  boxShadow: 3,
  boxBorderWeight: 4,
  boxBorderStyle: 5,
  cellFillColor: 6,
  cellTextItalic: 7,
  cellTextBold: 8,
  cellTextUnderline: 9,
  cellTextAlign: 10,
  cellFontColor: 11,
  cellTextVAlign: 12,
  cellWrapText: 13,
  insertRowAbove: 14,
  insertRowBelow: 15,
  insertColumnLeft: 16,
  insertColumnRight: 17,
  deleteRow: 18,
  deleteColumn: 19,
  fieldDisplayMode: 20,
  fontSize: 21,
  fontName: 22,
  connectingLineColor: 23,
  connectingLineThickness: 24,
  boxLayout: 25,
  branchStyle: 26,
  boxPadding: 27,
  mgrVSpacing1: 28,
  leftSpacing: 29,
  rightSpacing: 30,
  topSpacing: 31,
  bottomSpacing: 32,
  spacingMode: 34,
  boxWidth: 35,
  boxMinWidth: 36,
  boxMaxWidth: 37,
  boxHeight: 38,
  contentAlign: 39,
  boxType: 40,
  cellExpandRow: 41,
  picFrame: 42,
  contentLock: 43,
  layoutLock: 44,
  hierLock: 45,
  persistLock: 46,
  cellRefType: 47,
  connectingLineDash: 48,
  leftMargin: 49,
  rightMargin: 50,
  topMargin: 51,
  bottomMargin: 52,
  mgrVSpacing2: 53,
  boxDashStyle: 54,
  subGroupLineDash: 55,
  clearcomgr: 56,
  setcomgr: 57,
  picMask: 58,
  boxFlag: 59,
  boxOverrides: 999 //This is a special case!
};
export const enumHorzCentering = {
  userplacement: 0,
  centermiddle: 1,
  pinLeft: 2,
  pinCenter: 3,
  pinRight: 4
};
export const enumVertCentering = {
  userplacement: 0,
  centertop: 1,
  centermiddle: 2,
  pinTop: 3,
  pinBottom: 4
};
export const enumHorzPositioning = { stayLeft: 0, stayRight: 1 };
export const enumVertPositioning = { stayTop: 0, stayBottom: 1 };
export const enumBGAreaLookup = {
  [`${enumHorzCentering.pinLeft}${enumVertCentering.pinTop}`]: "topLeft",
  [`${enumHorzCentering.pinCenter}${enumVertCentering.pinTop}`]: "topMiddle",
  [`${enumHorzCentering.pinRight}${enumVertCentering.pinTop}`]: "topRight",
  [`${enumHorzCentering.pinLeft}${enumVertCentering.pinBottom}`]: "bottomLeft",
  [`${enumHorzCentering.pinCenter}${enumVertCentering.pinBottom}`]: "bottomMiddle",
  [`${enumHorzCentering.pinRight}${enumVertCentering.pinBottom}`]: "bottomRight",
  [`${enumHorzCentering.userplacement}${enumVertCentering.userplacement}`]: "customPlacement"
};
export const enumBoxType = { standard: 0, nonstandard: 1 };
export const enumBoxSubTypes = {
  chartBox: 0,
  textBox: 1,
  autoText: 2,
  picture: 3,
  refBoxTable: 4,
  refBoxLegend: 5,
  refMRBox: 6,
  refChart: 7,
  enumeratedTable: 8,
  holderAreaBox: 9
};
export const enumAutoTypeTypes = { pagenumbers: 10, dates: 11, titles: 13, tables: 14 };
export const enumSubChartFlag = { nobreak: 0, drillup: 1, drilldown: 2 };
export const enumContainerType = { nonmrb: -1, standardmrb: 0, comgr: 1 };
export const enumColInsert = { insertLeft: "insertcolleft", insertRight: "insertcolright" };
export const kStandardMargins = "S";
export const kDenseMargins = "D";
export const kManualMargins = "M";
export const kStandardMarginsStr = "Standard";
export const kDenseMarginsStr = "Dense";
export const kManualMarginsStr = "Manual";
export const kDefaultBoxPadding = 4;
export const kDefaultCellHeight = 14;
export const kDefaultFontSize = 11;
export const kBoxShadowOffset = 4;
export const enumDashStyle = { nodash: 0, stddash: 1, nodraw: 2 };
export const enumFlagType = { none: 0, triupperright: 1 };
export const kTriAnnoW = 24;
export const kTriAnnoWCalc = Math.sqrt(2 * kTriAnnoW * kTriAnnoW) / 2;
export const kMiniTriWidth = kTriAnnoW / 2;
export var enumBoxShapes = {
  rectangle: 0,
  roundRectangle: 1,
  snipCornerRectangle: 2,
  roundSnipCornerRectangle: 3,
  boxGroup: 4
};
export var kBoxShapes = [
  enumBoxShapes.rectangle,
  enumBoxShapes.roundRectangle,
  enumBoxShapes.snipCornerRectangle,
  enumBoxShapes.roundSnipCornerRectangle
];
export var kBoxShapeCodes = ["rectangle", "roundrect", "snipcornerrect", "roundcornerrect"];
export var kMinLastColWidth = 12;
export const kMinBoxHeight = 1;
export const kMaxBoxHeight = 959;
export const enumBoxWidthMode = {
  fixedwidth: 0,
  autoself: 1,
  autosmart: 2,
  autochildren: 3
};
export const enumApplyTo = { entirechart: 0, currentsubchart: 1, selection: 2 };
export var enumBoxHeightMode = { fixedheight: 0, autoself: 1, autosmart: 2 };
export const enumBoxStyleSet = {
  systemboxstyles: 0,
  sharedboxstyles: 1,
  userboxstyles: 2
};
export const kStartUID = 5000;
export const kStartPUID = 6000;
export const enumBoxStyleSearch = { currentSet: 0, allSets: 1 };
export var kBoxStylesMarginV = 24;
export var kBoxStylesMarginH = 16;
export var kBSRightClickOptions = ["Edit", "Delete", "Duplicate", "Rename"];
export var kBSRightClickCmds = [
  "EditBoxStyle",
  "OKDeleteBoxStyles",
  "DuplicateBoxStyle",
  "RenameBoxStyle"
];
export var kBSRightClickIcons = ["editobj", "deleteobj", "duplicateobj", "renameobj"];
export var GROUPBOX_MARGIN = 5;
export var AUXLINE_MARGIN = 6;
export var DRAW_GRID = false;
export const kHorzBoxSpacing = 6;
export const kVertBoxSpacing = 6;
export const enumBranchStyle = {
  tree: 0,
  stagger: 17,
  banded: 18,
  twocolumn: 1,
  fourcolumn: 2,
  sixcolumn: 3,
  eightcolumn: 4,
  twelvecolumn: 14,
  sixteencolumn: 15,
  twentycolumn: 16,
  onecolumncenterright: 5,
  onecolumncenterleft: 6,
  onecolumnright: 7,
  onecolumnleft: 8,
  swimlane: 19,
  assistantpeerright: 10,
  assistantpeerleft: 11,
  assistantright: 12,
  assistantleft: 13,
  index: -1
};
export const kPeerAssistBranchGroup = -1;
export const kStdAssistBranchGroup = -2;
export var enumAlignStyle = { left: 0, right: 1, center: 2, alternate: 3 };
export var WALL_THICKNESS = 5;
export var ONE_COLUMN_OFFSET = 12;
export var SHADOW_OFFSET = 2;
export var ADORNMENT_MARGIN = 6;
export var COMANAGER_HEIGHT = 12;
export var SNAP_CENTER_DISTANCE = 25;
export var CARD_LAYOUT_SPACING = 20;
export var needToRecenterToPage = false;
export var enumBoxDragMode = {
  dragModeOff: 0,
  moveBranch: 1,
  moveChart: 2,
  unlinkBranch: 3
};
export var enumDropType = {
  none: 0,
  leftcoworker: 1,
  rightcoworker: 2,
  subordinate: 3,
  mrbadd: 4,
  mrbpop: 5,
  drillunder: 6,
  drillup: 7
};
export var enumPageSizeUnits = { inches: 0, pixels: 1 };
export var gOptimizeDebug = false;
export var enumScalingMethod = {
  lockaspect: 0,
  unlockaspect: 1,
  scalehorz: 2,
  scalevert: 3,
  scaletopage: 4
};

export var kOrgPrefsBkgrnd = "";
export var kDropDownMarginH = 20;
export var kRefCellRightClickOptions = ["scopechart", "scopebranch"];
export var kRefCellRightClickCmds = ["scopechart", "scopebranch"];
export var kRefCellRightClickIcons = ["addformula", "addformula"];
export const enumBoxOverlay = {
  add: 0,
  move: 1,
  moveout: 2,
  delete: 3,
  lock: 4,
  change: 5,
  comment: 6,
  filter: 7,
  mute: 8,
  wand: 9
};
export const kOverlaySize = 12;
export var kBoxResizerSize = 6;
export var kHalfBoxResizerSize = 3;
export var kBoxExpanderSize = 8;
export var kHalfBoxExpanderSize = 4;
export const ChartBuilderPageSizes = [
  ["letterps", "LetterPS"],
  ["legalps", "LegalPS"],
  ["tabloidps", "TabloidPS"],
  ["A0", "A0PS"],
  ["A1", "A1PS"],
  ["A2", "A2PS"],
  ["A3", "A3PS"],
  ["A4", "A4PS"]
];
export const kChartLevels = {
  2: 2,
  3: 3,
  4: 4,
  5: 5,
  6: 6,
  7: 7,
  8: 8,
  9: 9,
  10: 10,
  "-1": "All Levels"
};
export const kQueryBuilderDDWidth = 250;
export const kMonthToString = {
  "01": "january",
  "02": "february",
  "03": "march",
  "04": "april",
  "05": "may",
  "06": "june",
  "07": "july",
  "08": "august",
  "09": "september",
  10: "october",
  11: "november",
  12: "december"
};
export const enumDocSections = {
  "Subcharts & Shortcuts": "Subcharts",
  Profile: null,
  Presentation: "View"
};
export const UItoHLConversion = {
  0: { invalid: "chartsettings", valid: "documentsettings" },
  1: { invalid: "documentsettings", valid: "chartsettings" }
};
export const enumSectionFromUIMode = { documentsettings: enumDocSections };
export var kBorderStyleCodes = ["noborder", "singleborder", "dblborder"];
export var kBorderDashCodes = ["solidborder", "dashedborder"];
export var kLineDashCodes = ["solidline", "dashedline", "hideline"];
export const enumChartTemplateSet = {
  systemtemplates: 0,
  sharedtemplates: 1,
  usertemplates: 2,
  chartselfview: 3
};
export const kBoxStyleRule = "boxStyle";
export const kPositionBoxStyleRule = "posBoxStyle";
export const kBoxOverlayRule = "boxOverlay";
export const kPositionBoxOverlayRule = "posBoxOverlay";
export const kFieldOverlayRule = "fieldOverlay";
export const kPositionFieldOverlayRule = "posFieldOverlay";
export const kBranchStyleOverlayRule = "branchStyle";
export const kMasterPageRule = "masterPage";
export const kGroupingRule = "grouping";
export const kHighlightingRule = "boxHighlight";
export const kDynHighlightRule = "dynamicHighlight";
export const kInheritedRule = "inheritTemplate";
export const enumBSFlagOptions = {
  notset: -1,
  comgr: 0,
  assistleft: 1,
  assistright: 2,
  leftpeerassist: 3,
  rightpeerassist: 4,
  treestyle: 5,
  stagger: 6,
  banded: 7,
  twocolumn: 8,
  fourcolumn: 9,
  sixcolumn: 10,
  eightcolumn: 11,
  twelvecolumn: 12,
  sixteencolumn: 13,
  twentycolumn: 14,
  onecolumncenterright: 15,
  onecolumncenterleft: 16,
  onecolumnright: 17,
  onecolumnleft: 18
};
export const enumRuleCriteria = {
  "Always Apply": 0,
  "Basic Rule": 1,
  "Complex Rule": 2
};

export const bsNameMap = {
  [enumBSFlagOptions.treestyle]: "tree",
  [enumBSFlagOptions.stagger]: "stagger",
  [enumBSFlagOptions.banded]: "banded",
  [enumBSFlagOptions.twocolumn]: "twocolumn",
  [enumBSFlagOptions.fourcolumn]: "fourcolumn",
  [enumBSFlagOptions.sixcolumn]: "sixcolumn",
  [enumBSFlagOptions.eightcolumn]: "eightcolumn",
  [enumBSFlagOptions.twelvecolumn]: "twelvecolumn",
  [enumBSFlagOptions.sixteencolumn]: "sixteencolumn",
  [enumBSFlagOptions.twentycolumn]: "twentycolumn",
  [enumBSFlagOptions.onecolumncenterright]: "onecolCR",
  [enumBSFlagOptions.onecolumncenterleft]: "onecolCL",
  [enumBSFlagOptions.onecolumnright]: "onecolR",
  [enumBSFlagOptions.onecolumnleft]: "onecolL",
  [enumBSFlagOptions.assistleft]: "leftassist",
  [enumBSFlagOptions.assistright]: "rightassist",
  [enumBSFlagOptions.rightpeerassist]: "rightpassist",
  [enumBSFlagOptions.leftpeerassist]: "leftpassist"
};

export const bsFlagMap = {
  tree: enumBSFlagOptions.treestyle,
  stagger: enumBSFlagOptions.stagger,
  banded: enumBSFlagOptions.banded,
  twocolumn: enumBSFlagOptions.twocolumn,
  fourcolumn: enumBSFlagOptions.fourcolumn,
  sixcolumn: enumBSFlagOptions.sixcolumn,
  eightcolumn: enumBSFlagOptions.eightcolumn,
  twelvecolumn: enumBSFlagOptions.twelvecolumn,
  sixteencolumn: enumBSFlagOptions.sixteencolumn,
  twentycolumn: enumBSFlagOptions.twentycolumn,
  onecolumncenterright: enumBSFlagOptions.onecolumncenterright,
  onecolumncenterleft: enumBSFlagOptions.onecolumncenterleft,
  onecolumnright: enumBSFlagOptions.onecolumnright,
  onecolumnleft: enumBSFlagOptions.onecolumnleft,
  swimlane: enumBSFlagOptions.treestyle,
  assistantpeerright: enumBSFlagOptions.rightpeerassist,
  assistantpeerleft: enumBSFlagOptions.leftpeerassist,
  assistantright: enumBSFlagOptions.assistright,
  assistantleft: enumBSFlagOptions.assistleft
};
export var gUIFont = "Arial";
export var gCUIFont = "";
export const kSchedulerWidth = 400;
export const kSchedDDWidth = 150;

export const kRowSecDefinitionPanelHeight = 600;

export const kRowSecDefinitionPanelWidth = 600;
export const kSchedFreq = ["daily", "weekly"];
export const kTimeList = [
  "00 (12 AM)",
  "01 (1 AM)",
  "02 (2 AM)",
  "03 (3 AM)",
  "04 (4 AM)",
  "05 (5 AM)",
  "06 (6 AM)",
  "07 (7 AM)",
  "08 (8 AM)",
  "09 (9 AM)",
  "10 (10 AM)",
  "11 (11 AM)",
  "12 (12 PM)",
  "13 (1 PM)",
  "14 (2 PM)",
  "15 (3 PM)",
  "16 (4 PM)",
  "17 (5 PM)",
  "18 (6 PM)",
  "19 (7 PM)",
  "20 (8 PM)",
  "21 (9 PM)",
  "22 (10 PM)",
  "23 (11 PM)"
];
export const kDayList = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];
// Mirrors PIT_MAX_ARCHIVE_AGE_DAYS in ServerSide/umongometa.py: the server prunes at this age
// whatever the chart configured, so the UI must not offer a longer retention.
export const kPITMaxArchiveAgeDays = 365;

export const mouseSettingsArray = ["Off", "zoom", "vscroll", "hscroll"];
export const themeModeSettingsArray = ["lightmode", "darkmode"];
export const mfaModeSettingsArray = ["Authenticator", "Off"];
export var kCLSize = 4;
export const kADPHeaders = [
  "Labor Union",
  "Assignment Term",
  "Pay Cycle Code",
  "Pay Grade Code",
  "Mobile",
  "Salary",
  "Pay Cycle",
  "Hourly Rate",
  "FTE",
  "Position ID",
  "Worker Type",
  "Worker Code",
  "Hire Date",
  "Benefits Eligibility Class",
  "Assigned Shift",
  "Home Cost Number",
  "Home Department Name",
  "City Name",
  "Postal Code",
  "State Code",
  "County Code",
  "Country Code",
  "Seniority Date",
  "FLSA",
  "Pay Grade",
  "Nickname",
  "Payroll Group Code",
  "Currency",
  "Recent Hire Date",
  "Rate 2",
  "Years of Service",
  "Years in Position",
  "Management Position",
  "Birthdate",
  "File #",
  "Annual Salary",
  "Normalized Salary",
  "Job Class",
  "Work Country Code",
  "Gender",
  "Ethnicity",
  "Long Title",
  "Race",
  "Age",
  "Officer Type",
  "Badge ID",
  "Job Function",
  "Education Level",
  "Salary Effective Date",
  "Full Legal Name",
  "Home Location ID"
].sort();

export const kBambooHeaders = [
  { alias: "workEmail", name: "Work Email" },
  { alias: "workPhone", name: "Work Phone" },
  { alias: "mobilePhone", name: "Mobile Phone" },
  { alias: "gender", name: "Gender" },
  { alias: "hireDate", name: "Hire Date" },
  { alias: "payRate", name: "Pay Rate" },
  { alias: "payGroup", name: "Pay Group" },
  { alias: "payGroupId", name: "Pay Group ID" },
  { alias: "payType", name: "Pay Type" },
  { alias: "workPhoneExtension", name: "Work Phone Extension" }
].sort(function (a, b) {
  return a.name.localeCompare(b.name);
});

export const kPaycomHeaders = [
  { alias: "allocation_consecdt", name: "Allocation Consecdt" },
  { alias: "allocation_consecot", name: "Allocation Consecot" },
  { alias: "allocation_dailydt", name: "Allocation Dailydt" },
  { alias: "allocation_dailyot", name: "Allocation Dailyot" },
  { alias: "allocation_garciaot", name: "Allocation Garciaot" },
  { alias: "allocation_regrate", name: "Allocation Regrate" },
  { alias: "allocation_weeklydt", name: "Allocation Weeklydt" },
  { alias: "allocation_weeklyot", name: "Allocation Weeklyot" },
  { alias: "annual_salary", name: "Annual Salary" },
  { alias: "dist_1_acct_code", name: "D1 Acct Code" },
  { alias: "dist_1_amount", name: "D1 Amount" },
  { alias: "dist_1_rout_code", name: "D1 Rout Code" },
  { alias: "dist_1_type_code", name: "D1 Type Code" },
  { alias: "dist_2_acct_code", name: "D2 Acct Code" },
  { alias: "dist_2_amount", name: "D2 Amount" },
  { alias: "dist_2_rout_code", name: "D2 Rout Code" },
  { alias: "dist_2_type_code", name: "D2 Type Code" },
  { alias: "dist_3_acct_code", name: "D3 Acct Code" },
  { alias: "dist_3_amount", name: "D3 Amount" },
  { alias: "dist_3_rout_code", name: "D3 Rout Code" },
  { alias: "dist_3_type_code", name: "D3 Type Code" },
  { alias: "dist_4_acct_code", name: "D4 Acct Code" },
  { alias: "dist_4_amount", name: "D4 Amount" },
  { alias: "dist_4_rout_code", name: "D4 Rout Code" },
  { alias: "dist_4_type_code", name: "D4 Type Code" },
  { alias: "dist_5_acct_code", name: "D5 Acct Code" },
  { alias: "dist_5_amount", name: "D5 Amount" },
  { alias: "dist_5_rout_code", name: "D5 Rout Code" },
  { alias: "dist_5_type_code", name: "D5 Type Code" },
  { alias: "dist_6_acct_code", name: "D6 Acct Code" },
  { alias: "dist_6_amount", name: "D6 Amount" },
  { alias: "dist_6_rout_code", name: "D6 Rout Code" },
  { alias: "dist_6_type_code", name: "D6 Type Code" },
  { alias: "dist_7_acct_code", name: "D7 Acct Code" },
  { alias: "dist_7_amount", name: "D7 Amount" },
  { alias: "dist_7_rout_code", name: "D7 Rout Code" },
  { alias: "dist_7_type_code", name: "D7 Type Code" },
  { alias: "dist_8_acct_code", name: "D8 Acct Code" },
  { alias: "dist_8_amount", name: "D8 Amount" },
  { alias: "dist_8_rout_code", name: "D8 Rout Code" },
  { alias: "dist_8_type_code", name: "D8 Type Code" },
  { alias: "excessive_gross", name: "Excessive Gross" },
  { alias: "excessive_net", name: "Excessive Net" },
  { alias: "hourly_salary", name: "Hourly Salary" },
  { alias: "last_pay_rate", name: "Last Pay Rate" },
  { alias: "net_acct_code", name: "Net Acct Code" },
  { alias: "net_rout_code", name: "Net Rout Code" },
  { alias: "net_type_code", name: "Net Type Code" },
  { alias: "rate_1", name: "Rate 1" },
  { alias: "rate_2", name: "Rate 2" },
  { alias: "rate_3", name: "Rate 3" },
  { alias: "rate_4", name: "Rate 4" },
  { alias: "rate_5", name: "Rate 5" }
].sort(function (a, b) {
  return a.name.localeCompare(b.name);
});
export const kSageDefaultQuery =
  "SELECT ECF_Employment_Type__c, fHCM2__Email__c, fHCM2__First_Name__c,  fHCM2__Name_Full__c, fHCM2__Surname__c, fHCM2__Unique_Id__c, Name, Tenure__c, UDF_Department__c, UDF_Department_Number__c, UDF_Manager_ID__c, UDF_Organization__c, UDF_Region__c, UDF_Work_Location__c, ECF_Start_Date__c, fHCM2__Job_Title__c, fHCM2__Employment_Status__c from fHCM2__Team_Member__c";
export const kDayforceTables = [
  "EmployeeProperties",
  "PayGradeRates",
  "EmploymentStatuses",
  "OrgUnitInfos",
  "Ethnicities",
  "PerformanceRatings",
  "Contacts",
  "UnionMemberships",
  "Addresses"
].sort();
export const kUKGTables = [
  "personDetails",
  "compensationDetails",
  "userDefinedFields",
  "orgDetails",
  "platformConfigurationFields"
];
export const kUKGReadyTables = ["Base Compensation", "Total Compensation", "HR Custom Fields"];
export const kISolvedTables = [
  { alias: "Alternate Rates", name: "includeAlternateRates" },
  { alias: "Misc. Fields", name: "includeMiscFields" },
  { alias: "Organizations", name: "includeOrganizations" }
];
export const kPaychexTables = [
  { alias: "Compensation", name: "compensation" },
  { alias: "Custom Fields", name: "customfields" },
  { alias: "Locations", name: "locations" }
];
export const kADPFields = [
  "Age",
  "Annual Salary",
  "Assignment Term",
  "Assigned Shift",
  "Badge ID",
  "Benefits Eligibility Class",
  "Birthdate",
  "City Name",
  "Country Code",
  "Country Subdivision Level 1",
  "Country Subdivision Level 2",
  "Currency",
  "Department",
  "Department Code",
  "Education Level",
  "Ethnicity",
  "File #",
  "First Name",
  "FLSA",
  "FTE",
  "Gender",
  "Hire Date",
  "Home Cost Number",
  "Home Department Name",
  "Hourly Rate",
  "Job Class",
  "Job Class Name",
  "Long Title",
  "Job Function",
  "Job Function Code",
  "Management Position",
  "Mobile",
  "Nickname",
  "Normalized Salary",
  "Officer Type",
  "Pay Cycle",
  "Pay Cycle Code",
  "Pay Grade",
  "Pay Grade Code",
  "Payroll Group Code",
  "Position ID",
  "Postal Code",
  "Preferred First Name",
  "Preferred Gender Pronoun",
  "Race",
  "Rate 2",
  "Recent Hire Date",
  "Salary",
  "Salary Effective Date",
  "Seniority Date",
  "State Code",
  "Status",
  "Labor Union",
  "Work Country Code",
  "Worker Code",
  "Worker Type",
  "Years in Position",
  "Years of Service",
  "Full Legal Name",
  "Home Location ID"
].sort();
export const kADPEnterpriesFields = [
  "Annual Salary",
  "Assignment Term",
  "Currency",
  "FLSA",
  "FTE",
  "File #",
  "Gender",
  "Mobile",
  "Normalized Salary",
  "Pay Cycle Code",
  "Pay Grade",
  "Pay Grade Code",
  "Postal Code",
  "Salary",
  "State Code",
  "Status",
  "Home Location ID",
  "Full Legal Name"
].sort();
export const kdetailedADPFields = [
  "Age",
  "Benefits Eligibility Class",
  "Birthdate",
  "Ethnicity",
  "Hire Date",
  "Hourly Rate",
  "Labor Union",
  "Officer Type",
  "Recent Hire Date",
  "Seniority Date",
  "Years in Position",
  "Years of Service"
].sort();
export const kavailableVantageFields = [
  "Assignment Code",
  "City Name",
  "Country Code",
  "Country Subdivision Level 1",
  "Country Subdivision Level 2",
  "Currency",
  "File #",
  "FLSA",
  "FTE",
  "Gender",
  "Home Cost Number",
  "Home Department Name",
  "Long Title",
  "Management Position",
  "Mobile",
  "Nickname",
  "Payroll Group Code",
  "Position ID",
  "Postal Code",
  "Preferred First Name",
  "Seniority Date",
  "Status",
  "Worker Country Code",
  "Worker Code",
  "Worker Type",
  "Home Location ID",
  "Full Legal Name"
].sort();
export const kdetailedVantageFields = [
  "DEPTID",
  "DEPTID Code",
  "Pay Grade",
  "Pay Grade Code",
  "Officer Type Code",
  "Officer Type Name",
  "Officer Type",
  "PAYDEPTID",
  "PAYDEPTID Code",
  "Hire Date",
  "Ethnicity",
  "Years of Service"
].sort();
export const kADPHeaderMap = {
  ADP: kADPFields,
  "ADP NG HCM": kADPFields,
  "WFN Next Generation": kADPFields,
  "ADP Canada": kADPFields,
  "ADP Enterprise": kADPEnterpriesFields,
  Vantage: kavailableVantageFields
};
export const kCornerstoneAdditionalHeaders = [
  { alias: "Product Area", name: "Product Area" },
  { alias: "BU", name: "BU" },
  { alias: "Division", name: "Division" },
  { alias: "Company", name: "Company" },
  { alias: "Location Type", name: "Location Type" },
  { alias: "Sub Group", name: "Sub Group" }
].sort(function (a, b) {
  return a.name.localeCompare(b.name);
});

export var kMsgCanvasWidth = 320;
export var kMsgCanvasHeight = 80;
export const enumExcelExportScope = {
  entirechart: 0,
  currentbranch: 1,
  currentpage: 2,
  orphanrecords: 3,
  toporphans: 4,
  entirechartplus: 5,
  redeployed: 6,
  section508: 7
};
export var kCustomExcelWidth = 294;
export const kSpecialFolders = [
  "charts",
  "data",
  "masters",
  "photos",
  "samples",
  "tutorials",
  "trash",
  "backgrounds"
];
export const enumFileMgrMode = {
  open: 0,
  saveas: 1,
  upload: 2,
  uploadIW: 3,
  importfile: 4,
  selectmasterSA: 5,
  selectmasterIW: 6,
  selectmasterRP: 7,
  importfileSA: 8,
  selectimage: 9,
  selectcellimage: 10,
  comparefile: 11,
  mergefile: 12,
  savemaster: 13,
  xls: 14,
  csv: 15,
  excel: 16,
  charts: 17,
  xml: 18,
  json: 19,
  manual: 20,
  openmaster: 21,
  replaceimage: 22,
  selectbackground: 23,
  selectcustomchart: 24
};
export const enumFileFilterMode = {
  all: 0,
  charts: 1,
  backgrounds: 2,
  flatfiles: 3,
  images: 4
};
export var kFlatFileList = ["xls", "csv", "xlsx", "xml", "txt", "json"];
export var kCompareDirsBkgrnd = "";
export var kCompareDirsWidth = 260;
export var enumFolderType = { std: 0, sample: 2, trash: 4, publicf: 6, stdlock: 24 };

export var gHelpRef = {
  "": [],
  root: [],

  // VIEWER
  viewerHelp: ["viewer.html"],
  filterHelp: ["viewer", "filters.html"],
  highlightHelp: ["viewer", "highlights.html"],

  // GENERAL CHART SETTINGS
  rpdatasource: ["chart-settings", "source.html"],
  Mapping: ["chart-settings", "mapping.html"],
  "Fields & Formulas": ["chart-settings", "fields-and-formulas.html"],
  Presentation: ["chart-settings", "presentation.html"],
  Profile: ["chart-settings", "profile.html"],
  "Filters & Highlights": ["chart-settings", "filters-and-highlights.html"],
  highlight_example: ["Samples", "Dynamic_Highlighting.htm"],
  "Chart Builder": ["chart-settings", "chart-builder.html"],
  "Presentation Options": ["chart-settings", "chart-builder.html"],
  "Saved Filters": ["chart-settings", "chart-builder.html"],
  "Search & Directory": ["chart-settings", "search-and-directory.html"],
  Publish: ["chart-settings", "publish.html"],
  Refresh: ["chart-settings", "refresh.html"],
  "Point In Time": ["chart-settings", "point-in-time-charts.html"],
  "Archived Charts": ["orgchart-viewer", "workbench-menu", "orgchart-builder.html"],
  "Subcharts & Shortcuts": ["chart-settings", "subcharts---shortcuts.html"],

  // GENERAL ACCOUNT SETTINGS
  "Account Settings": ["orgchart-viewer", "more-menu", "account-settings.html"],
  General: ["account-settings", "general.html"],
  "Base Charts": ["account-settings", "chart-management.html"],
  Theme: ["account-settings", "theme.html"],
  accessgroups: ["account-settings", "access-groups.html"],
  accountsettings_ag_config: [],
  Security: ["account-settings", "security.html"],
  Authorization: ["account-settings", "authorization.html"],
  manageusers: ["account-settings", "manage-users.html"],
  "UI Profiles": ["account-settings", "ui-profiles.html"],
  Checkpoints: ["account-settings", "checkpoints.html"],

  // WORKBENCH
  workbenchHelp: ["workbench.html"],
  stagingHelp: ["top-toolbar", "staging.html"],
  chartEditorHelp: ["workbench", "chart-editor.html"],

  // DOCUMENT OPTIONS
  pagePropsHelp: ["workbench", "document-menu", "page-properties.html"],
  manageFieldsHelp: ["workbench", "document-menu", "field-manager.html"],
  comparedoc_help: ["workbench", "document-menu", "compare-charts.html"],
  filemgr_help: ["workbench", "document-menu", "file-manager.html"],
  tutorial_help: ["workbench", "document-menu", "create.html"],

  // DOCUMENT SETTINGS
  docSetChanges: ["workbench", "document-settings", "changes.html"],
  docSetrpdatasource: ["workbench", "document-settings", "source.html"],
  docSetFilter: ["workbench", "document-settings", "filter.html"],
  docSetMapping: ["workbench", "document-settings", "mapping.html"],
  docSetAssistants: ["workbench", "document-settings", "assistants.html"],
  docSetSubcharts: ["workbench", "document-settings", "subcharts.html"],
  docSetView: ["workbench", "document-settings", "view.html"],
  "docSetFields & Formulas": ["workbench", "document-settings", "fields---formulas.html"],
  docSetBackground: ["workbench", "document-settings", "background.html"],
  docSetRefresh: ["workbench", "document-settings", "refresh.html"],
  docSetAdvanced: ["workbench", "document-settings", "advanced.html"],
  "docSetPage Properties": ["workbench", "document-settings", "page-properties.html"],

  // BACKGROUND OPTIONS
  background_help: ["background-editor.html"],

  // REFRESH OPTIONS
  scheduler_help: ["workbench", "refresh-menu", "scheduler.html"],
  refresh_help: ["workbench", "refresh-menu", "refresh.html"],
  datasourcew_help: ["workbench", "refresh-menu", "source.html"],
  assistantw_help: ["workbench", "refresh-menu", "assistants.html"],
  ctemplatesw_help: ["workbench", "refresh-menu", "template-wizard.html"],
  subchartw_help: ["workbench", "refresh-menu", "subcharts.html"],
  masterpgw_help: ["workbench", "refresh-menu", "master-wizard.html"],
  refreshadv_help: ["workbench", "refresh-menu", "advanced.html"],
  datafilter_help: ["workbench", "refresh-menu", "filter.html"],
  mapping_help: ["workbench", "refresh-menu", "mapping.html"],

  //PRESENTATION OPTIONS
  boxstylesHelp: ["workbench", "presentation-menu.html"],
  templatesHelp: ["workbench", "presentation-menu.html"],
  masterEditorHelp: ["workbench", "presentation-menu", "master-editor.html"],

  // VIEW MANAGER
  rulesHelp: ["view-manager", "rules.html"],
  manageBoxStylesHelp: ["view-manager", "box-styles.html"],
  layoutEditorHelp: ["view-manager", "box-styles", "box-layout-editor.html"],
  templateRuleHelp: ["view-manager", "rules", "rule-editor.html"],

  // PLANNING
  planningHelp: ["planning.html"],
  planexplorer: ["planning", "plan-explorer.html"],
  planbuilder: ["planning", "creating-plans.html"],
  planTalentHelp: ["planning", "talent-pool.html"],
  planUpdateHelp: ["planning", "updating-plans.html"],
  planDashboardHelp: ["planning", "plan-dashboard.html"],
  planActivityHelp: ["planning", "activity-log.html"],
  planFieldsHelp: ["planning", "creating-plans", "editable-fields.html"],
  planMetricsHelp: ["planning", "creating-plans", "plan-metrics.html"],
  planEffDateHelp: ["planning", "creating-plans", "effective-dating.html"],
  plansharing: ["planning", "creating-plans", "sharing-plans.html"],

  // DIRECTORY
  directoryHelp: ["directory.html"],

  // APPEND TABLE
  appendTableEditorHelp: ["connectors", "append-table.html"],
  appendAddHelp: ["connectors", "append-table", "add-new-record.html"],
  appendEditHelp: ["connectors", "append-table", "edit-existing-record.html"],
  appendMoveHelp: ["connectors", "append-table", "move-record.html"],
  appendSuppressHelp: ["connectors", "append-table", "suppress-record.html"],

  // PUB LINKS
  userAuthHelp: ["topics", "restricted-link-setup.html"],

  // CONNECTORS
  help_bamboo: ["connectors", "bamboohr", "bamboohrconnector-setup--workbench-.html"],
  //"help_odbc": ["connectors","odbc.html"],
  adp_help: ["connectors", "adp", "adp-legacy-configuration.html"],
  sutiHR_help: ["connectors", "sutihr.html"],
  successfactors_wb_help: ["connectors", "successfactorsconnector-setup--workbench-.html"],
  trinet_help: ["connectors", "trinet", "trinet-connector-setup.html"],
  orange_help: ["connectors", "orangehrmconnector-setup--workbench-.html"],
  orangehrm_viewer_help: ["connectors", "orangehrm", "orangehrm-connector.html"],
  custom_help: ["contact-us.html"],
  jobvite_help: ["connectors", "jobvite", "jobvite-connector-setup.html"],
  google_directory_help: ["connectors", "google-directory.html"],
  activedir_help: ["connectors", "active-directory.html"],
  excel_help: ["connectors", "excel.html"],
  csv_help: ["connectors", "csv.html"],
  sftp_pull_help: ["connectors", "sftp-pull.html"],
  adp_workforce_now_help: ["connectors", "adp", "adp-workforce-now.html"],
  adp_vantage_help: ["connectors", "adp", "adp-vantage.html"],
  adp_enterprise_help: ["connectors", "adp", "adp-enterprise.html"],
  "adp_wfn_next generation_help": ["connectors", "adp", "adp-workforce-now-next-generation.html"],
  "adp_wfn_next_hcm generation_help": ["connectors", "adp", "adp-next-gen-hcm.html"],
  adp_recruit_management_help: ["connectors", "adp-recruitment", "adp-recruitment-management.html"],
  adp_wfn_recruitment_module_help: [
    "connectors",
    "adp-recruitment",
    "adp-wfn-recruitment-module.html"
  ],
  adp_faq_help: ["connectors", "adp.html"],
  bamboohr_help: ["connectors", "bamboohr", "bamboohr-connector-setup.html"],
  sapxml_help: ["connectors", "sap.html"],
  saprfc_help: ["connectors", "sap.html"],
  odbc_help: ["connectors", "odbc.html"],
  ultipro_help: ["connectors", "ukg-pro", "ukg-pro-connector--workbench-.html"],
  ukg_pro_help: ["connectors", "ukg-pro--api-", "ukg-connector-setup.html"],
  ukg_help: ["connectors", "ukg", "ukg-connector-setup.html"],
  "ukg_pro_(raas)_help": ["connectors", "ukg-pro--raas-", "ukg-pro--raas--connector.html"],
  ukg_ready_help: ["connectors", "ukg-ready", "ukg-ready-connector-setup.html"],
  salesforce_help: ["connectors", "salesforce", "salesforce-connector-setup.html"],
  successfactors_help: ["connectors", "successfactors", "successfactors-configuration.html"],
  payspace_help: ["connectors", "payspace.html"],
  paychex_help: ["connectors", "paychex", "paychex-connector-setup.html"],
  paycom_help: ["connectors", "paycom", "paycom-connector-setup.html"],
  paycor_help: ["connectors", "paycor.html"],
  namely_help: ["connectors", "namely", "namely-connector-setup.html"],
  workday_help: ["connectors", "workday", "workday-connector.html"],
  signify_help: ["connectors", "signify", "signify-connector-setup.html"],
  dayforce_help: ["connectors", "dayforce", "dayforce-connector-setup.html"],
  unicornhro_help: ["connectors", "unicorn-hro.html"],
  azuread_help: ["connectors", "azure-ad", "azure-ad-connector-setup.html"],
  azureadphotos_help: ["connectors", "azure-ad", "azure-ad-photo-configuration.html"],
  igloophotos_help: ["connectors", "photo-integrations", "igloo-photo-configuration.html"],
  workdayphotos_help: ["connectors", "workday", "workday-photo-import.html"],
  oraclephotos_help: ["connectors", "photo-integrations", "oracle-photo-configuration.html"],
  subscribeHR_help: ["connectors", "subscribe-hr", "subscribe-hr-connector-setup.html"],
  //"lucca_help": ["connectors","lucca.html"],
  paylocity_help: ["connectors", "paylocity.html"],
  oracle_help: ["connectors", "oracle", "oracle-connector-setup.html"],
  "sage-300-za_help": ["connectors", "sage-300-za", "sage-300-connector-setup.html"],
  sage_help: ["connectors", "sage", "sage-connector-setup.html"],
  orangehrm_help: ["connectors", "orangehrm-connector.html"],
  peoplehr_help: ["connectors", "peoplehr", "peoplehr-connector-setup.html"],
  breathehr_help: ["connectors", "breathe-hr", "breathe-hr-connector-setup.html "],
  clear_company_help: ["connectors", "clearcompany", "clearcompany-connector-setup.html"],
  cornerstone_help: ["connectors", "cornerstone.html"],
  ocnapi_help: ["connectors", "ocn-api.html"],
  lever_help: ["connectors", "lever", "lever-connector-setup.html"],
  oth_help: ["connectors", "ey-oth.html"],
  icims_help: ["connectors", "icims", "icims-connector-setup.html"],
  append_table_help: ["connectors", "append-table.html"],
  append_plan_help: ["planning", "append-plan.html"],
  greenhouse_help: ["connectors", "greenhouse", "greenhouse-connector-set-up.html"],
  dynchart_help: ["connectors", "dynchart-connector.html"],
  manual_help: ["connectors.html"], // TODO @Michael - can reuse this for when manual connector is set up in the help documentation - ["connectors","manual-connector.html"]
  thrive_trm_help: ["connectors", "thrive-trm", "thrive-trm-connector-setup.html"],
  // OTHER
  workbench: ["orgchart-workbench"],
  chartsettings: ["Reference_Guide", "OrgChart_Viewer", "OrgChart_Viewer.htm#toc7"],
  main_help: ["index-en.html"],
  contactus: ["contact-us.html"],
  subscription: ["orgchart-overview", "terms-of-service-and-privacy-policy.html"],
  viewsettings: ["orgchart-viewer", "more-menu", "view-settings.html"],
  release_notes: ["release-notes.html"],
  news_help: [],

  howto_help: ["topics.html"],
  importexcel_help: ["connectors", "excel-connector.html"],

  definefield_help: [],

  profile_help: ["top-toolbar", "profile.html"],
  subchart_help: ["orgchart-workbench", "side-panels", "subcharts-panel.html"],
  search_help: ["orgchart-workbench", "side-panels", "search-panel.html"],
  views_help: ["orgchart-workbench", "side-panels", "views-panel.html"],
  viewer_views: ["viewer", "views.html"],
  filters_help: ["topics", "filters-and-highlights.html"],
  parking_help: ["orgchart-workbench", "side-panels", "parking-panel.html"],
  ctemplates_help: ["orgchart-workbench", "side-panels", "templates-panel.html"],
  multichart_help: ["topics", "multi-chart-documents.html"],
  topbox_help: ["topics", "co-head-example.html"],
  complexfilter_help: [],
  enterspacing_help: [],
  boxspacing_help: [],
  enterval_help: [],
  enterarule_help: [],
  entercrule_help: [],
  entercond_help: [
    "topics",
    "security-profiles",
    "row-level-security",
    "conditional-row-level-security.html"
  ],
  dynamic_formula_edit: ["topics", "formulas", "dynamic-formulas.html"],
  formula_type_help: [],
  formula_span_help: [],
  formula_field_help: [],
  referenceTableHelp: ["topics", "reference-table-example.html"],

  searchdir_help: [],
  manageusers_help: [
    "orgchart-workbench",
    "top-toolbar",
    "administration-tools",
    "manage-users.html"
  ],
  managegroups_help: [
    "orgchart-workbench",
    "top-toolbar",
    "administration-tools",
    "manage-groups.html"
  ],
  publisher_help: ["top-toolbar", "export-menu.html"],
  web_publisher_help: ["top-toolbar", "export-menu", "web-publishing.html"],

  map_help: ["Topics", "A-F", "Formulas", "MAP.htm"],
  math_help: ["Topics", "A-F", "Formulas", "MATH.htm"],
  min_help: ["Topics", "A-F", "Formulas", "MIN.htm"],
  max_help: ["Topics", "A-F", "Formulas", "MAX.htm"],
  lookup_help: ["Topics", "A-F", "Formulas", "LOOKUP.htm"],
  concat_help: ["Topics", "A-F", "Formulas", "CONCATENATE.htm"],
  right_help: ["Topics", "A-F", "Formulas", "RIGHT.htm"],
  left_help: ["Topics", "A-F", "Formulas", "LEFT.htm"],
  sum_help: ["Topics", "A-F", "Formulas", "SUM.htm"],
  average_help: ["Topics", "A-F", "Formulas", "AVERAGE.htm"],
  sumif_help: ["Topics", "A-F", "Formulas", "SUMIF.htm"],
  averageif_help: ["Topics", "A-F", "Formulas", "AVERAGEIF.htm"],
  countif_help: ["Topics", "A-F", "Formulas", "COUNTIF.htm"],
  companyname_help: [],
  subchartarrow_help: [
    "How-To_Articles",
    "Working",
    "Accounts_and_Security",
    "Account_Preferences",
    "Subchart_Arrow_Style.htm"
  ],
  accountPrefs_help: [
    "orgchart-workbench",
    "top-toolbar",
    "administration-tools",
    "preferences.html"
  ],
  commPrefs_help: ["topics", "system-alerts.html"],
  locking_help: ["topics", "box-locking-example.html"],

  importtrouble_help: [],
  ctemplateedit_help: [],
  ctemplateeditor_help: [],

  filer_help: ["orgchart-workbench", "top-toolbar", "home-ribbon.html"],
  refreshr_help: ["orgchart-workbench", "top-toolbar", "refresh-ribbon.html"],
  editchartr_help: ["orgchart-workbench", "top-toolbar", "chart-ribbon.html"],
  editmasterr_help: ["orgchart-workbench", "top-toolbar", "master-ribbon.html"],
  sharer_help: ["orgchart-workbench", "top-toolbar", "format-ribbon.html"],
  subchartsHelp: ["top-toolbar", "subcharts.html"],
  documentr_help: ["orgchart-workbench", "top-toolbar", "document-ribbon.html"],
  planr_help: ["orgchart-workbench", "top-toolbar", "extras-ribbon.html"],
  sort_help: [],
  boxlayout_help: [
    "Reference_Guide",
    "OrgChart_Workbench",
    "Side_Panels",
    "Box_Styles_Panel",
    "Box_Layout_Editor",
    "Box_Layout_Editor.htm"
  ],
  comanagers_help: [
    "How-To_Articles",
    "Working",
    "Working_with_Charts",
    "Formatting_Charts",
    "Branch_Styles.htm"
  ],
  branchstyle_help: [
    "How-To_Articles",
    "Working",
    "Working_with_Charts",
    "Formatting_Charts",
    "Branch_Styles.htm"
  ],
  optbranch_help: [
    "How-To_Articles",
    "Working",
    "Working_with_Charts",
    "Formatting_Charts",
    "Branch_Styles.htm"
  ],
  accesspriv_help: [
    "orgchart-workbench",
    "top-toolbar",
    "document-ribbon",
    "access-privileges.html"
  ],

  quickstart_help: ["Getting_Started", "Quick_Start_Guide", "Quick-Start_Guide.htm"],
  userfeed_help: [
    "How-To_Articles",
    "Working",
    "Accounts_and_Security",
    "User_Management",
    "Mass_Creation_of_User_Accounts.htm"
  ],

  ppt_help: [],
  pdf_help: [],
  placeholder_help: [],
  querybuilder_help: [],

  templaterule_help: ["topics", "complex-filters-example.html"],
  load_archive_help: ["chart-settings", "point-in-time-charts.html"],
  staticformulaeditor: ["topics", "formulas", "static-formulas.html"],
  securityHelp: [
    "topics",
    "security-profiles",
    "row-level-security",
    "conditional-row-level-security.html"
  ],
  branchLevelHelp: ["topics", "security-profiles", "branch-level-security.html"],

  vieweradmin: ["quick-start-guides", "quick-start--admin-.html"], // for enterprise
  viewerenduser: ["quick-start-guides", "quick-start--end-user-.html"], // for adp

  builderpanel: ["orgchart-viewer", "workbench-menu", "orgchart-builder.html"],
  rpadvoptions: ["orgchart-viewer", "workbench-menu", "orgchart-builder.html"],
  filterdata: ["orgchart-viewer", "workbench-menu", "orgchart-builder", "defining-filters.html"],

  optionsFilter: ["orgchart-viewer", "options-menu", "filters.html"],
  localization: ["orgchart-viewer", "options-menu", "filters.html"],

  dynamic_localization: ["topics", "dynamic-localization.html"],
  privacypolicy: ["legal", "privacy-policy.html"],
  termsofservice: ["legal", "subscription-agreement.html"],
  singlesignonhelp: ["topics", "sso-configuration.html"],
  restrictedLinkHelp: ["account-settings", "authorization.html#configuring-a-restricted-link-user"],
  sharePointHelp: ["topics", "automatically-export-to-sharepoint.html"],
  ssoConfigHelp: ["topics", "single-sign-on", "sso-configuration"],
  shortcutsHelp: ["top-toolbar", "shortcuts.html"],
  advancedSearchHelp: ["top-toolbar", "search", "advanced-search-configuration"],
  SearchHelp: ["top-toolbar", "search.html"],
  aggregatedFieldHelp: ["topics", "formulas", "aggregated-fields.html"],
  importFilterHelp: ["topics", "import-filters.html"],
  multiFactorAuth: ["topics", "multi-factor-authentication.html"],

  mapping_photo_config: ["connectors", "photo-integrations.html#additional-photo-configuration"],

  Builder: ["top-toolbar", "builder.html"]
};
export var kToolTipTimer = 500;
export const enumWizPanelMode = {
  selectfile: 0,
  filterdata: 1,
  requiredfields: 2,
  subchartwizIW: 3,
  selecttemplate: 4,
  selectmasterIW: 5,
  dataerror: 6,
  keyerror: 7,
  filtererror: 8,
  filterdataSA: 9,
  requiredfieldsSA: 10,
  subchartwizSA: 11,
  selecttemplateSA: 12,
  selectmasterSA: 13,
  selectfileSA: 14,
  subcharterror: 15,
  datarefreshSA: 16,
  selectmasterRP: 18,
  advancedOptions: 19,
  assistwizIW: 20,
  assistwizSA: 21,
  selectADPProfile: 22
};
export const enumSubChartOption = {
  nobreak: 0,
  autobreaks: 1,
  breakbylevel: 2,
  breakbyfield: 3,
  renameall: 4,
  keepAsIs: 99
};
export const enumSCDensity = { standard: 0, dense: 1, verydense: 2 };
export const enumAssistOption = { manual: 0, byfield: 1, byrule: 2 };
export const enumBGOption = { custom: 0, system: 1, linked: 2 };
export const enumRefreshOption = { smartrefresh: 0, stdrefresh: 1, customrefresh: 2 };
export var kIWHeight = 425;
export var kIWWidth = 640;
export var kIWHeaderHeight = 38;
export var kIWFont = "Verdana";
export var kIWHorzMargin = 20;
export var kIWButtonGap = 10;
export var kIWVSpacing = 10;
export var kIWTopMargin = 6;
export var kIWDropDownWidthS = 150;
export var kIWDropDownWidthL = 220;
export var kIWDropDownWidthL2 = 280;
export var kIWLabelVOffset = 50;
export var kReqFieldDDSpacing = 28;
export const kSCOptionsRadioGroup = [
  ["nobreaks", "IWNoSubCharts"],
  ["autobreaks", "IWSubChartAuto"],
  ["breakbylevel", "IWSubChartByLevel"],
  ["breakbyfield", "IWSubChartByField"]
];
export var enumMasterSelectMode = { importwiz: 0, standalone: 1, refreshprops: 2 };
export var kCellAlignWidth = 75;
export var kSizingModeDropDownWidth = 125;
export const kSizingModeOptions = ["Fixed", "autoself", "autosmart", "autochildren"];
export var kLONumWidth = 54;
export const kGutterMax = 40;

export const kTriangleWidth = 10;
export var kMaxBoxSize = 8 * 96 - kTriangleWidth / 2 + 2;
export var kMinTrips = 2;
export var kSegmentInterval = 50;
export var kNumSegments = 8;

export const kPadSizeY = 10;
export const enumUIColors = {
  Default: ["#3d3d4a", "#64646F", "#222331"],
  "Blue Gray": ["#0E5061", "#106074", "#0b404d"],

  Red: ["#911c0a", "#ae210c", "#741608"],

  Orange: ["#A33D1D", "#C34922", "#823017"],

  Green: ["#1d6f42", "#22854f", "#175834"],
  "Forest Green": ["#4c6334", "#5b763e", "#3c4f29"],

  Blue: ["#005b99", "#006db7", "#00487a"],
  "Cobalt Blue": ["#2a5699", "#3267b7", "#21447a"],

  Purple: ["#631e94", "#7624b1", "#4f1876"],
  Orchid: ["#7719AA", "#8e1ecc", "#5f1488"],

  Black: ["#262626", "#2d2d2d", "#1e1e1e"],
  Gray: ["#636363", "#636363", "#4f4f4f"]
};
export const enumPageSizes = {
  Letter: [816, 1056],
  Legal: [816, 1344],
  Tabloid: [1056, 1632],
  A0: [3179, 4494],
  A1: [2245, 3179],
  A2: [1587, 2245],
  A3: [1123, 1587],
  A4: [794, 1123],
  ppt1ps: [720, 960],
  ppt2ps: [540, 960]
};

export const kConnectorDict = {
  main: kEnterpriseConnectorList,
  append: kAppendConnectorList,
  merge: kMergeConnectorList
};

export const kDefaultFieldGroups = {
  Core: [
    "PersonID",
    "SupervisorID",
    "Name",
    "Title",
    "aTitle",
    "First Name",
    "Division",
    "Preferred First",
    "Last Name",
    "Preferred Last",
    "Location"
  ],
  Diversity: ["Age", "Race", "Ethnicity", "Gender", "Pronouns", "Country of Origin"],
  Contact: ["Work Phone", "Home Phone", "Personal Email", "Work Email"],
  Compensation: ["Salary", "Base Salary", "Bonus", "Bonus Target", "Currency", "Frequency"],
  Location: ["City", "State", "Address", "Zip", "Country"],
  Personal: ["Birthday", "Favorite Color", "T-Shirt Size", "Fun Fact", "Bio"],
  Custom: []
};
export const kPDFPageSizes = [
  ["Letter", "LetterPS", 612, 792],
  ["Legal", "LegalPS", 612, 1008],
  ["Tabloid", "TabloidPS", 792, 1224],
  ["A0", "A0PS", 2384, 3370],
  ["A1", "A1PS", 1684, 2384],
  ["A2", "A2PS", 1191, 1684],
  ["A3", "A3PS", 842, 1191],
  ["A4", "A4PS", 595, 842],
  ["Custom", "CustomPS", -1, -1]
];
export var kOOBkgrnd = "";
export var kSmallOOWidth = 240;
export var kOOFolderPickerLabelWidth = 180;
export var kSmallPubOWidth = 240;
export var enumChartTypes = {
  standard: 0,
  inverted: 1,
  level: 2,
  starburst: 3,
  horseshoe: 4,
  card: 5
};
export var kIndentEdge = 4;
export var gNodeID = 0;
export const enumPageOrientationOptions = { landscape: 0, portrait: 1 };
export const kStdHeaderHeight = 0.6;
export const kStdFooterHeight = 0.3;
export const enumCorePageSize = {
  letter: 0,
  legal: 1,
  tabloid: 2,
  a0: 3,
  a1: 4,
  a2: 5,
  a3: 6,
  a4: 7
};
export var enumUnitVal = { pixels: 0, inches: 1, cm: 2 };
export var enumPagePropsTE = {
  pageWidth: 0,
  pageHeight: 1,
  marginLeft: 2,
  marginTop: 3,
  marginRight: 4,
  marginBottom: 5,
  headerHeight: 6,
  footerHeight: 7
};
export var kPageSizeRadioGroup = [
  ["letterps", "LetterPS", 8.5, 11],
  ["legalps", "LegalPS", 8.5, 14],
  ["tabloidps", "TabloidPS", 11, 17],
  ["a0ps", "A0PS", 33.1, 46.8],
  ["a1ps", "A1PS", 23.4, 33.1],
  ["a2ps", "A2PS", 16.5, 23.4],
  ["a3ps", "A3PS", 11.7, 16.5],
  ["a4ps", "A4PS", 8.27, 11.69],
  ["ppt1ps", "PPT1", 7.5, 10],
  ["ppt2ps", "PPT2", 5.63, 10],
  ["ppt3ps", "PPT3", 6.25, 10],
  ["customps", "CustomPS", -1, -1]
];
export var enumPageSizeValues = { label: 0, cmdCode: 1, pageWidth: 2, pageHeight: 3 };
export var kMarginRadioGroup = [
  ["extrawidem", "ExtraWideMargin", 0.75, 0.75, 0.75, 0.75],
  ["widem", "WideMargin", 0.5, 0.5, 0.5, 0.5],
  ["standardm", "StandardMargin", 0.25, 0.25, 0.25, 0.25],
  ["narrowm", "NarrowMargin", 0.1, 0.1, 0.1, 0.1],
  ["customm", "CustomMargin", -1, -1, -1, -1]
];
export var enumMarginValues = {
  label: 0,
  cmdCode: 1,
  left: 2,
  top: 3,
  right: 4,
  bottom: 5
};
export var kOrientationRadioGroup = [
  ["landscape", "Landscape"],
  ["portrait", "Portrait"]
];
export var kHeaderRadioGroup = [
  ["standardh", "StandardHeader"],
  ["noheader", "NoHeader"],
  ["customh", "CustomHeader"]
];
export var kFooterRadioGroup = [
  ["standardf", "StandardFooter"],
  ["nofooter", "NoFooter"],
  ["customf", "CustomFooter"]
];
export var kChartTypeOptsRadioGroup = [
  ["Standard (Top-Down)", "ChartType-Standard"],
  ["Inverted (Bottom-Up)", "ChartType-Inverted"],
  ["Level (Management Level Bands)", "ChartType-Level"],
  ["Starburst", "ChartType-Starburst"],
  ["Horseshoe", "ChartType-Horseshoe"]
];
export const enumOverrideType = {
  add: "Add",
  move: "Move",
  moveout: "Move Out",
  delete: "Delete",
  lock: "Lock",
  change: "Change",
  comment: "Comment"
};
export var planningBodyFont = "12px Verdana";
export const kPlanningSubPanelWidth = 400;
export var enumProfileRowType = {
  standard: 0,
  freeTextSingle: 1,
  freeTextDouble: 2,
  object: 3
};
export var enumProfileColType = { label: 0, value: 1, freetext: 2, object: 3 };

export const enumOptimizedMode = { enumForPrint: 0, enumForScreen: 1 };
export const enumPubScopes = ["Page", "Branch", "Book", "MultiChart"];
export var enumRenderStep = { openandclose: 0, openpage: 1, closepage: 2 };
export var enumBookType = { pdf: 0, xml: 1, ppt: 2, googleslides: 3 };
export var enumBookScope = { page: 0, branch: 1, entire: 2, alldivisions: 4 };
export var enumPageStep = { masterStep: 0, contentStep: 1 };
export const kMaxStringLength = 32;
export var enumSpacingCtrlName = [
  "leftmargin",
  "rightmargin",
  "topmargin",
  "bottommargin",
  "mgrspacing1",
  "mgrspacing2",
  "leftspacing",
  "rightspacing",
  "topspacing",
  "bottomspacing"
];
export var kNumSpacingCtrls = 10;
export var kSmallSAButtonHeight = 18;
export var kSpacingAdjWidth = 250;
export var kSpacingDraggerH = 14;
export var kSpacingHeaderH = 18;
export var kSpacingControlH = 16;
export var kMaxVSpacing = 384;
export var kSpacingAdjElementType = 2000;
export var enumSpacingDiagram = { margin: 0, mgrspacing: 1, spacing: 2 };
export var kSIHeight = 100;
export var kSARightClickOptions = ["Reset"];
export var kSARightClickCmds = ["RCResetSpacing"];
export var kSARightClickIcons = ["reloadtemplates"];
export var gLastParam = "";
export var gOptimizeX = null;
export var gOptimizeY = null;
export var enumBranchPref = { tree: 0, twocolumn: 1, fourcolumn: 2 };
export const kDefaultAspectRatio = 5;
export const kAspectRatioLookup = [0.5, 0.66, 0.8, 1.0, 1.3, 1.6, 1.8, 2.0, 2.5, 3.2, 4.0];
export const kMasterSCID = 0;
export const kIndexSCID = -1;
export const kIndexPageLabelWidth = 300;
export const kTextDefSize = 12;
export const kFunctionalLabelWIdth = 320;
export var gVerbosePODebug = false;
export const kMaxSCSize = 50;
export const kMaxSpanOfControl = 12;
export var kReloadMasterPage = 1;
export const enumSCArrowVariants = {
  miniarrow: 0,
  whitearrow: 1,
  blackarrow: 2,
  themearrow: 3
};
export var kSCArrowOffset = 4;
export const MasterConnectorList = [
  "Active Directory",
  "ADP RUN",
  "ADP TotalSource",
  "ADP WFN Canada",
  "ADP Workforce Now",
  "ADP Vantage",
  "ADP Enterprise",
  "AzureAD",
  "BambooHR",
  "BreatheHR",
  "Cornerstone",
  "Dayforce",
  "Google Directory",
  "Namely",
  "NaturalHR",
  "ODBC",
  "OrangeHRM",
  "Oracle",
  "Paycor",
  "Paychex",
  "Lucca",
  "Paylocity",
  "Paylocity NextGen",
  "NetSuite",
  "Paycom",
  "PaySpace",
  "PeopleHR",
  "PeopleSoft",
  "PowerSchool",
  "Salesforce",
  "SAP",
  "Subscribe-HR",
  "SuccessFactors",
  "SuitHR",
  "Signify",
  "TriNet",
  "UltiPro",
  "Workday",
  "UnicornHRO",
  "ADP Decidium",
  "ADP Link",
  "UKG Pro",
  "UKG Ready",
  "ADP WFN Netherlands",
  "ADP iHCM2",
  "iSolved",
  "ADP WFN Next Generation",
  "ADP Next Gen HCM",
  "Jemini"
];
export var kConnectorOptionsAdmin = MasterConnectorList.sort().concat(["Other", "Manual"]);
export const MasterATSList = [
  "Greenhouse",
  "ADP Recruit Management",
  "ADP WFN Recruitment Module",
  "ADP WFN CA Recruitment Module",
  "iCIMS",
  "Jobvite",
  "Taleo",
  "Cornerstone",
  "Lever",
  "Ultipro",
  "Workday",
  "AzureAD",
  "BambooHR",
  "ResNav",
  "ClearCompany",
  "Oracle Recruiting"
];
export var ATSOptions = MasterATSList.sort().concat(["Other", "Manual", "None"]);
export const kSourceOptions = [
  "OWS USA",
  "OWS UK",
  "Fusion5",
  "OCH",
  "i-RH",
  "Proverb",
  "OrgChart Experts",
  "HCMNext",
  "Focus Solutions",
  "Unknown"
];
export var kTermOptions = [
  "Annual",
  "Monthly",
  "Quarterly",
  "6-Month",
  "2-Year",
  "3-Year",
  "5-Year",
  "Other"
];
export var kUserStatusDDWidth = 133;
export var WIGGLE_FACTOR = 10;
export var enumLinkTypeOptions = { publink: 0, privlink: 1, restlink: 2 };
export var enumLaunchTypeOptions = { takenoaction: 0, openmychart: 1, openfile: 2 };
export var enumLaunchModeOptions = { Portal: 0, Viewer: 1, Workbench: 2 };
export var enumZoomOptions = { zoom1: 0, zoom2: 1, zoom3: 2 };
export const kMaxFileSize = 1024 * 1024 * 40;
export const kMaxImageSize = 1024 * 1024 * 1;
export const enumUploadCode = {
  success: 0,
  successr: 1,
  cancel: 2,
  err: 3,
  maxsize: 4,
  invalidtype: 5
};

export var kFontSizeOptions = ["6", "7", "8", "9", "10", "11", "12", "14", "18", "24", "36"];
export var kMRBOptions = [
  "Create MRB",
  "Standard MRB",
  "Co-Manager MRB",
  "1 Column",
  "2 Column",
  "3 Column",
  "4 Column"
];
export var kSmallMRBPWidth = 110;
export var kSmallMRBPHeight = 142;
export var kSmallMRBPButtonWidth = kSmallMRBPWidth - 4;
export var kSmallMRBPButtonHeight = 18;

export const kOpenShortCut2 = 119;
export const kSaveShortCut2 = 120;
export const kCancelDialog = 27;
export const kEnterOKShortCut = 13;
export const kUndoShortCut = 122;
export const kRedoShortCut = 121;
export const kOpenShortCut = 111;
export const kSaveShortCut = 115;
export const kCloseShortCut = 119;
export const kNewShortCut = 110;
export const kBoxLayoutShortCut = 123;
export const kPasteShortCut = 118;
export const kCopyShortCut = 99;
export const kCutShortCut = 120;
export const kItalicShortCut = 105;
export const kUnderlineShortCut = 117;
export const kBoldShortCut = 98;
export const kSelectAllShortCut = 97;
export const enumIconIndex = {
  newdoc: 0,
  save: 1,
  saveas: 2,
  opendoc: 3,
  closedoc: 106,
  undo: 5,
  redo: 6,
  print: 7,
  publish: 8,
  printpreview: 9,
  importwizard: 10,
  signoff: 11,
  about: 12,
  help: 13,
  paste: 14,
  copy: 15,
  copyformat: 16,
  cut: 17,
  lock: 18,
  pantool: 19,
  movebox: 20,
  movechart: 21,
  addroot: 22,
  addsub: 23,
  addassist: 24,
  textbox: 25,
  boxshape: 26,
  boxfill: 27,
  boxshadow: 28,
  boxborder: 29,
  boxbordercolor: 30,
  boxlayouteditor: 31,
  font: 32,
  fontsize: 33,
  fontcolor: 34,
  fillcolor: 35,
  bold: 36,
  italic: 37,
  underline: 38,
  labeloptions: 39,
  topalign: 40,
  middlealign: 41,
  bottomalign: 42,
  wraptext: 43,
  aligntextleft: 44,
  aligntextcenter: 45,
  aligntextright: 46,
  justify: 47,
  linecolor: 48,
  linethickness: 49,
  linepattern: 50,
  branchstyles: 51,
  optimizebranch: 52,
  boxspacing: 53,
  find: 54,
  replace: 55,
  deleteobj: 56,
  sort: 57,
  select: 58,
  selecttool: 58,
  arrange: 59,
  pageprops: 60,
  managefields: 61,
  marquee: 62,
  drawcellborder: 63,
  erasecellborder: 64,
  insertrowabove: 65,
  insertrowbelow: 66,
  insertcolumnleft: 67,
  insertcolumnright: 68,
  deleterows: 69,
  deletecolumns: 70,
  mergecells: 71,
  unmergecells: 72,
  cellbordercolor: 73,
  cellborderpattern: 74,
  cellborderthickness: 75,
  okbutton: 76,
  cancelbutton: 77,
  centercharthorz: 78,
  centerchartvert: 79,
  aligncharttop: 80,
  bringtofront: 81,
  sendtoback: 82,
  bringforward: 83,
  sendbackward: 84,
  alignleft: 85,
  aligncenter: 86,
  alignright: 87,
  aligntop: 88,
  alignmiddle: 89,
  alignbottom: 90,
  unlink: 91,
  trash: 106,
  addfolder: 93,
  deletefolder: 94,
  closedfolder: 95,
  openfolder: 96,
  closedsamplefolder: 97,
  opensamplefolder: 98,
  duplicatefile: 99,
  addfield: 100,
  deletefield: 101,
  portrait: 102,
  landscape: 103,
  renamefile: 104,
  renamefolder: 105,
  deletefile: 92,
  settings: 107,
  adduser: 108,
  deleteuser: 109,
  edituser: 110,
  spacingleft: 111,
  spacingright: 112,
  spacingabove: 113,
  spacingbelow: 114,
  mgrspacing: 115,
  backarrow: 116,
  forwardarrow: 117,
  svgdoc: 118,
  jpgdoc: 119,
  pngdoc: 120,
  fittopage: 121,
  sizetochart: 122,
  sizepagetochart: 123,
  apiinfo: 190,
  pdfdoc: 125,
  showrightpanel: 126,
  reloadtemplates: 127,
  edittemplates: 128,
  shuffleup: 129,
  shuffledown: 130,
  replacetemplate: 131,
  addtemplate: 132,
  deletetemplate: 133,
  home: 134,
  upload: 135,
  download: 136,
  editprofile: 137,
  importwizard2: 138,
  replaceobj: 139,
  addobj: 140,
  renameobj: 141,
  duplicateobj: 142,
  editobj: 143,
  applychanges: 7,
  manageusers: 145,
  pngdoc2: 146,
  transbackgnd: 147,
  addformula: 148,
  editformula: 149,
  samewidth: 150,
  sameheight: 151,
  croptochart: 152,
  masterpage: 153,
  normalview: 154,
  refreshdata: 155,
  refreshprops: 156,
  cloudconnectors: 157,
  insertsubchart: 158,
  removesubchart: 159,
  removeallsubcharts: 160,
  subchartwiz: 161,
  pinobj: 162,
  picture: 163,
  legend: 164,
  moveobj: 165,
  table: 166,
  cellfillcolor: 167,
  autotext: 168,
  applymaster: 169,
  expandrow: 170,
  scheduler: 171,
  roundpencil: 172,
  roundeye: 173,
  roundarrow: 174,
  roundchat: 175,
  roundx: 176,
  roundplus: 177,
  releases: 178,
  comment: 179,
  addgroup: 180,
  deletegroup: 181,
  updategroup: 182,
  outputoptions: 183,
  lockfolder: 184,
  maketop: 185,
  editmode: 172,
  reviewmode: 173,
  pptdoc: 188,
  querydatabase: 189,
  mychart: 190,
  alerts: 191,
  getstart: 192,
  frbold: 193,
  odbc: 194,
  lockbox: 195,
  topics: 196,
  tutorials: 197,
  videos: 198,
  onlinedocs: 199,
  urllink: 200,
  unzip: 201,
  xmldoc: 202,
  compare: 203,
  porgchart: 204,
  pdirectory: 205,
  activedir: 205,
  findbyface: 207,
  launch: 208,
  cloudfiles: 209,
  accesskey: 18,
  triangle: 211,
  process: 212,
  piechart: 213,
  filter: 214,
  greyscale: 215,
  flag: 216,
  newsletter: 217,
  grouping: 218,
  dogroup: 219,
  ungroup: 220,
  groupprops: 221,
  refreshfilter: 222,
  refreshfields: 223,
  refreshsource: 224,
  comgr: 225,
  picturemask: 226,
  picturesize: 227,
  savechanges: 144,
  smalldeleteobj: 228,
  moveouticon: 231
};
export const enumIconStr = {
  newdoc: 0,
  save: 1,
  saveas: 2,
  opendoc: 3,
  closedoc: 4,
  undo: 5,
  redo: 6,
  print: 7,
  publish: 8,
  printpreview: 9,
  exportexcel: 10,
  signoff: 11,
  about: 12,
  help: 13,
  paste: 14,
  copy: 15,
  copyformat: 16,
  cut: 17,
  selecttool: 58,
  pantool: 19,
  movebox: 20,
  movechart: 21,
  addroot: 22,
  addsub: 23,
  addassist: 24,
  textbox: 25,
  boxshape: 26,
  boxfill: 27,
  boxshadow: 28,
  boxborder: 29,
  boxbordercolor: 30,
  boxlayouteditor: 31,
  font: 32,
  fontsize: 33,
  fontcolor: 34,
  fillcolor: 35,
  bold: 36,
  italic: 37,
  underline: 38,
  labeloptions: 39,
  topalign: 40,
  middlealign: 41,
  bottomalign: 42,
  wraptext: 43,
  aligntextleft: 44,
  aligntextcenter: 45,
  aligntextright: 46,
  justify: 47,
  linecolor: 48,
  linethickness: 49,
  linepattern: 50,
  branchstyles: 51,
  optimizebranch: 52,
  boxspacing: 53,
  find: 54,
  replace: 55,
  deleteobj: 56,
  sort: 57,
  select: 58,
  arrange: 59,
  pageprops: 60,
  managefields: 61,
  marquee: 62,
  drawcellborder: 63,
  erasecellborder: 64,
  insertrowabove: 65,
  insertrowbelow: 66,
  insertcolumnleft: 67,
  insertcolumnright: 68,
  deleterows: 69,
  deletecolumns: 70,
  mergecells: 71,
  unmergecells: 72,
  cellbordercolor: 73,
  cellborderpattern: 74,
  cellborderthickness: 75,
  okbutton: 76,
  cancelbutton: 77,
  centercharthorz: 78,
  centerchartvert: 79,
  aligncharttop: 80,
  bringtofront: 81,
  sendtoback: 82,
  bringforward: 83,
  sendbackward: 84,
  alignleft: 85,
  aligncenter: 86,
  alignright: 87,
  aligntop: 88,
  alignmiddle: 89,
  alignbottom: 90,
  unlink: 91,
  trash: 92,
  addfolder: 93,
  deletefolder: 94,
  closedfolder: 95,
  openfolder: 96,
  closedsamplefolder: 97,
  opensamplefolder: 98,
  duplicatefile: 99,
  addfield: 100,
  deletefield: 101,
  portrait: 102,
  landscape: 103,
  renamefile: 104,
  renamefolder: 105,
  deletefile: 106,
  settings: 107,
  adduser: 108,
  deleteuser: 109,
  edituser: 110,
  spacingleft: 111,
  spacingright: 112,
  spacingabove: 113,
  spacingbelow: 114,
  mgrspacing: 115,
  backarrow: 116,
  forwardarrow: 117,
  svgdoc: 118,
  jpgdoc: 119,
  pngdoc: 120,
  fittopage: 121,
  sizetochart: 122,
  sizepagetochart: 123,
  epsdoc: 124,
  pdfdoc: 125,
  showrightpanel: 126,
  reloadtemplates: 127,
  edittemplates: 128,
  shuffleup: 129,
  shuffledown: 130,
  replacetemplate: 131,
  addtemplate: 132,
  deletetemplate: 133,
  home: 134,
  upload: 135,
  download: 136,
  editprofile: 137,
  importwizard: 138,
  replaceobj: 139,
  addobj: 140,
  renameobj: 141,
  duplicateobj: 142,
  editobj: 143,
  applychanges: 144,
  manageusers: 145,
  pngdoc2: 146,
  transbackgnd: 147,
  samewidth: 150,
  sameheight: 151,
  croptochart: 152,
  masterpage: 153,
  normalview: 154,
  refreshdata: 155,
  refreshprops: 156,
  cloudconnectors: 157,
  insertsubchart: 158,
  removesubchart: 159,
  removeallsubcharts: 160,
  subchartwiz: 161,
  pinobj: 162,
  picture: 163,
  legend: 164,
  moveobj: 165,
  table: 166,
  cellfillcolor: 167,
  autotext: 168,
  applymaster: 169,
  expandrow: 170,
  scheduler: 171,
  addformula: 148,
  editformula: 149,
  deletegroup: 181,
  addgroup: 180,
  updategroup: 182,
  outputoptions: 183,
  lockfolder: 184,
  maketop: 185,
  editmode: 172,
  reviewmode: 173,
  pptdoc: 189,
  apiinfo: 190,
  getstart: 192,
  frbold: 193,
  odbc: 194,
  lockbox: 195,
  topics: 196,
  tutorials: 197,
  videos: 198,
  onlinedocs: 199,
  urllink: 200,
  findbyface: 207,
  launch: 208,
  cloudfiles: 209,
  accesskey: 210,
  triangle: 211,
  process: 212,
  unzip: 201,
  xmldoc: 202,
  porgchart: 204,
  pdirectory: 205,
  picturemask: 227,
  piechart: 213,
  filter: 214,
  compare: 203,
  querydatabase: 189,
  mychart: 190,
  releases: 178,
  alerts: 191,
  newsletter: 217,
  grouping: 218,
  dogroup: 219,
  ungroup: 220,
  groupprops: 221,
  refreshfilter: 222,
  refreshfields: 223,
  refreshsource: 224,
  comgr: 225
};
export const enumIconIndex16 = {
  stdfolder: 0,
  openstdfolder: 1,
  samplesfolder: 2,
  opensamplesfolder: 3,
  trashfolder: 4,
  opentrashfolder: 5,
  publicfolder: 6,
  openpublicfolder: 7,
  file: 8,
  reset: 9,
  excelfile: 10,
  oc5file: 11,
  imagefile: 12,
  oc5mfile: 13,
  pdffile: 14,
  csvfile: 15,
  textfile: 16,
  texttype: 17,
  datetype: 18,
  numbertype: 19,
  sumformula: 20,
  picturetype: 21,
  genericfield: 22,
  formula: 23,
  stdfolderlock: 24,
  openstdfolderlock: 25,
  xmlfile: 26,
  zipfile: 27,
  encfile: 28
};
export const enumConnectorStringIndex96 = {
  excelapp: 0,
  adp: 1,
  bamboohr: 2,
  peoplehr: 3,
  sapxml: 4,
  sap: 5,
  odbc: 6,
  subscribehr: 7,
  ultipro: 8,
  sutihr: 9,
  salesforce: 10,
  breathehr: 11,
  successfactors: 12,
  google: 13,
  trinet: 14,
  orangehrm: 15,
  namely: 16,
  greenhouse: 17,
  jobvite: 19,
  icims: 20,
  activedir: 21,
  paylocity: 22,
  azure: 23,
  workday: 24,
  cornerstone: 25,
  custom: 26,
  signify: 27,
  payspace: 28,
  manualchart: 29,
  dayforce: 30,
  unicorn: 31,
  csv: 32,
  adpr: 33,
  lucca: 34,
  Oracle: 35,
  sage: 36,
  ocn: 37,
  ey: 38,
  lever: 39,
  sftp: 40,
  paycom: 41,
  paycor: 42,
  resnav: 43,
  paychex: 44,
  clearcompany: 45,
  ukg: 46,
  adprecmanagement: 47,
  thriveTRM: 48,
  adpng: 49,
  Jemini: 50,
  "Jade Star": 51,
  EmpowerHR: 52,
  DiyaHealth: 53,
  Igloo: 54
};
export const enumIconIndex256 = {
  tutorials: 0,
  newdoc: 1,
  stdfolder: 2,
  openstdfolder: 3,
  samplesfolder: 4,
  opensamplesfolder: 5,
  publicfolder: 6,
  openpublicfolder: 7,
  stdfolderlock: 8,
  openstdfolderlock: 9,
  samples: 10,
  uploadfile: 11,
  zipfile: 12,
  file: 13,
  textfile: 14,
  pdffile: 15,
  csvfile: 16,
  xmlfile: 17,
  encfile: 18,
  svgfile: 19,
  downloadfile: 20,
  importwiz: 21,
  jpgfile: 22,
  epsfile: 23,
  pdffile2: 24,
  hirespng: 25,
  docfile: 26,
  jpegfile: 27,
  pptfile: 28,
  xlsfile: 29,
  opendoc: 30,
  publishdoc: 31,
  imagefile: 32,
  picturetype: 33,
  oc5mfile: 34,
  genericfield: 35,
  sumformula: 36,
  texttype: 37,
  numbertype: 38,
  formula: 39,
  webinars: 40,
  getstart: 41,
  excelfile: 42,
  trashfolder: 43,
  opentrashfolder: 44,
  oc5file: 45,
  datetype: 46,
  reset: 47,
  releases: 48,
  newsletter: 49,
  alerts: 50
};
export const kLargePanelWidth = 1000;
export const kLargePanelHeight = 600;
export const kSpacing = 2;
export const kChartDocExtensions = ["ocd", "oc5"];
export const kBackgroundExtensions = ["ocb", "oc5m"];
export const kFontPickerOptions = [
  "Arial",
  "Comic Sans MS",
  "Courier",
  "Georgia",
  "Lucida Sans Unicode",
  "Palatino Linotype",
  "Tahoma",
  "Times",
  "Trebuchet MS",
  "Verdana",
  "SimSun"
];
export const kLabelOptions = ["Value only", "Label and value", "Label only"];
export const kDefaultKeyFields = ["First Name", "Last Name", "Title", "Division"];
export const kFreqOpts = ["Daily", "Weekly", "Monthly", "Off"];
export const kReservedFields = [
  "Span Of Control",
  "Subordinates",
  "Headcount",
  "Level",
  "SubChart Level",
  "Peers",
  "Also Reports To",
  "ID",
  "Position Count",
  "Subordinate Positions",
  "Direct Subordinate Positions",
  "Peer Positions"
];

export const restrictedInternalFields = [
  "Locked",
  "Persist",
  "scFlag",
  "ID",
  "_objInfo",
  "_internal",
  "treeID",
  "objType",
  "filled",
  "Holder Count"
];

export const internalOnlyFields = [
  "Locked",
  "Persist",
  "scFlag",
  "ID",
  "_objInfo",
  "_internal",
  "filled",
  "SYSTEM_CALCULATE",
  "_Span Of Control",
  "_Subordinates",
  "_Headcount",
  "_Level",
  "_SubChart Level",
  "_Peers",
  "treeID",
  "SuperIDLookup",
  "_Position Count",
  "_Subordinate Positions",
  "_Direct Subordinate Positions",
  "_Peer Positions"
];

export const kPersonID = "PersonID";
export const kNameID = "Name";
export const kDivisionID = "Division";
export const kTitleID = "Title";
export const kPhotoID = "Photo";
export const kAgentDefaultPhoto = "agent-default.png";
export const kAgentMarkers: readonly string[] = ["true", "1", "yes", "y"];
export const kSupervisorID = "SupervisorID";
export const kPhoneID = "Phone";
export const kEmailID = "Email";
export const kPeersID = "Peers";
export const kLevelID = "Level";

export const kLocationID = "Location"; // this field id is used for employee location for both position and employee charts
export const kEmployeeLocation = "Employee Location";
export const kEmployeeID = "EmployeeID";
export const kEmployeeSupervisorID = "Employee SupervisorID";
export const kEmployeePositionID = "Employee PositionID";
export const kEmployeeAssignmentStatusID = "Assignment Status";
export const kAuxSuperID = "AuxSuperID";
export const kOrphanID = "Orphan";

export const kPositionID = "PositionID";
export const kParentPositionID = "Parent PositionID";
export const kPositionLocation = "Position Location";
export const kPositionTitleID = "Position Title";
export const kPositionDivisionID = "Position Division";
export const kPositionStatusID = "Position Status";
export const kPositionStatus = "PositionStatus";

export const kObjtype = "objType";

export const kPositionStartDate = "Position Start Date";
export const kPositionEndDate = "Position End Date";

export const kPositionBudgetedSalary = "Position Budgeted Salary";
export const kPositionCostCenter = "Position Cost Center";
export const kPositionCostBandMinimum = "Position Cost Band Minimum";
export const kPositionCostBandMidpoint = "Position Cost Band Midpoint";
export const kPositionCostBandMaximum = "Position Cost Band Maximum";
export const kDesiredHolders = "Desired Holders";
export const kStaffingTarget = "Staffing Target";
export const kPositionHoldersCount = "Holder Count";
export const kPositionAllocation = "Allocation";
export const kEmployeeSalary = "Employee Salary";
export const kEmployeePay = "Employee Pay";
export const kEmployeeCostCenter = "Employee Cost Center";

export const kHeadcount = "Headcount";
export const kBudgetedHeadcount = "Budgeted Headcount";
export const kSubordinates = "Subordinates";
export const kSpanOfControl = "Span Of Control";
export const kPeers = "Peers";
export const kSalaryRollup = "Salary Rollup";
export const kCurrentPositionCost = "Current Position Cost";
export const kPositionBudgetRollup = "Position Budget Rollup";
export const kAllocation = "Allocation";
export const kAssignmentStatus = "Assignment Status";
export const kHolderCount = "Holder Count";

// Position-specific count fields
export const kPositionCount = "Position Count";
export const kSubordinatePositions = "Subordinate Positions";
export const kDirectSubordinatePositions = "Direct Subordinate Positions";
export const kPeerPositions = "Peer Positions";

export const kPositionCountFields = [
  kPositionCount,
  kSubordinatePositions,
  kDirectSubordinatePositions,
  kPeerPositions
] as const;

export const kEmployeeCountFields = [kHeadcount, kSubordinates, kSpanOfControl, kPeers] as const;

export const kPositionFieldIDs = [
  kPositionID,
  kParentPositionID,
  kPositionLocation,
  kPositionTitleID,
  kPositionDivisionID,
  kDesiredHolders,
  kPositionBudgetedSalary,
  kPositionCostCenter,
  kPositionCostBandMinimum,
  kPositionCostBandMidpoint,
  kPositionCostBandMaximum
];

export const kHolderFieldIDs = [
  kEmployeePositionID,
  kNameID,
  kEmployeeID,
  kTitleID,
  kEmployeeLocation,
  kEmployeeSalary,
  kEmployeePay,
  kEmployeeCostCenter
];

export const SYSTEM_CALCULATE = "SYSTEM_CALCULATE";
export const SYSTEM_GENERATED_AUTHOR = "system_generated";

export const SYSTEM_GENERATED_CAPABLE_FIELDS = [
  kDesiredHolders,
  kPositionCostBandMinimum,
  kPositionCostBandMidpoint,
  kPositionCostBandMaximum,
  kPositionBudgetedSalary
];

export const COST_BAND_FIELDS = [
  kPositionCostBandMinimum,
  kPositionCostBandMidpoint,
  kPositionCostBandMaximum
];

export const enumPositionDataIngestMode = {
  single: "Single",
  multi: "Multi",
  multiSingle: "MultiSingle"
};

export const enumPositionGenerationMode = { derived: "Synthetic", native: "Organic" };

export const kUIProfileOptions = {
  fCanExportExcel: { command: "excelExport", string: "enableexcel" },
  //fCanExportGoogleSlides: {
  //command: "googleSlidesExport",
  //string: "enablegoogleslides"
  //},
  fCanFilemgr: { command: "fileManager", string: "File manager" },
  fChartExplorer: { command: "chartExplorer", string: "Chart Explorer" },
  fCanPublish: { command: "canPublish", string: "ExportChart" },
  fHasProfile: { command: "profile", string: "showprofpanel" },
  fEnableProfileStarring: { command: "profileStarring", string: "Enable profile starring" },
  fHasSubcharts: { command: "subcharts", string: "showscpanel" },
  fHasSearch: { command: "search", string: "searchdir" },
  fHasDirectory: { command: "directory", string: "Directory" },
  fHasPlanning: { command: "planning", string: "Planning" },
  fHasReporting: { command: "reporting", string: "Reporting" },
  fEnableAbout: { command: "about", string: "Application, Account, User, and Chart information" },
  fEnableHelp: { command: "help", string: "Help" },
  fEnableSignOut: { command: "signoff", string: "signout" },
  fDefaultLanguage: { command: "enableLanguage", string: "Allow language change" },
  //fShowFilename: { command: "showFilenames", string: "showFilenames" },
  fShowCurrentUser: { command: "showusername", string: "Show Username" },
  //fDoHoverProfile: { command: "doHoverProfile", string: "Hover Profile" },
  fHasWorkbench: { command: "hasWorkbench", string: "Create custom charts" },
  fSelfLaunch: { command: "selflaunch", string: "Launch to Self" },
  fBreadCrumbs: { command: "breadCrumbs", string: "BreadCrumbs" },
  fRWUsersCanRefresh: {
    command: "fRWUsersCanRefresh",
    string: "Refresh base charts (Read/Write users)"
  },
  //fEnablePhotoSearch: { command: "photosearch", string: "Allow Photo Search" },
  fShowGrouping: { command: "showGrouping", string: "Show grouping" },
  fInteractiveFields: { command: "enableFields", string: "enableFields" },
  fWebLinks: { command: "createWebLink", string: "createWebLink" },
  fRecordDetails: { command: "recordDetails", string: "recordDetails" },
  fModifyBackgrounds: { command: "modifyBackgrounds", string: "Modify backgrounds" },
  fPositionMgmt: { command: "positionMgmt", string: "Position Management" },
  fPackaging: { command: "packaging", string: "Pricing and Packaging" },
  fShareFilters: { command: "shareFilters", string: "Share Filters & Highlights" },
  fSidebarFilters: { command: "sidebarFilters", string: "Filters" },
  fSidebarHighlights: { command: "sidebarHighlights", string: "Highlights" },
  fSidebarBackground: { command: "sidebarBackground", string: "Background" },
  fSidebarEmployeeList: { command: "sidebarEmployeeList", string: "Employee List" },
  fHasSettings: { command: "settings", string: "Settings" },
  fHasPIT: { command: "pit", string: "Point in Time" },
  fHasViews: { command: "views", string: "Views" },
  fHasNavigation: { command: "navigation", string: "Navigation Sidebar" }
};

export var kLaunchModes = ["Viewer", "Directory", "Workbench"];
export const kUserPlanRoles = ["collaborator", "viewer", "owner"];
export const kUserEditRoles = ["collaborator", "owner"];
export const enumPlanRoles = { collaborator: "collaborator", viewer: "viewer", owner: "owner" };
export const enumShareRoles = {
  owner: "owner",
  collaborator: "collaborator",
  viewer: "viewer"
} as const;
export const kUserShareRoles = ["collaborator", "viewer"] as const;
export const enumPlanStatus = {
  active: "Active",
  locked: "Locked",
  archived: "Archived"
} as const;
export const kDefaultPlanFields = [
  "PersonID",
  kSupervisorID,
  "Name",
  "Title",
  "Division",
  "Location"
];
export const kFieldOptionsDDWidth = 150;
export const kFieldOptions = ["Text", "Number", "Image", "Date", "Object"];
export const kDecimalOptions = ["0", "1", "2", "3", "4", "5"];
export const kFieldTypeIcons = ["", "", "", "", ""];
export const kHyperlinkOptions = ["notlinked", "hyperlink", "Email", "phone", "skype"];
export var enumIconButtonSizing = { small: 0, large: 1, medium: 2, xlarge: 3, xxlarge: 4 };
export var enumPinCmds = [
  "pinLeftTop",
  "pinCenterTop",
  "pinRightTop",
  "pinLeftBottom",
  "pinCenterBottom",
  "pinRightBottom",
  "pinOff"
];
export const kIndent = 10;
export var kLevelOptions = ["2", "3", "4", "5"];
export var kSmartBreakOptions = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
  "15",
  "20"
];
export var kStartLevelOptions = ["2", "3", "4"];
export const kCBBoxLayoutContent = "BOXF";
export const kCBBoxOverrideContent = "BOXO";
export const kCBChartBoxContent = "BOXES";
export const kDocVersion = 3;
export const enumDataSource = {
  manual: 0,
  excel: 1,
  bamboohr: 2,
  workday: 3,
  peoplehr: 4,
  ultipro: 5,
  odbc: 6,
  sapxml: 7,
  adpworkforcenow: 8,
  subscribehr: 9,
  sutihr: 10,
  salesforce: 11,
  breathehr: 12,
  activedir: 13,
  cornerstone: 14,
  successfactors: 15,
  custom: 16,
  googledir: 17,
  trinet: 18,
  payspace: 19,
  orangehrm: 20,
  sap: 21,
  namely: 22,
  paylocity: 23,
  greenhouse: 24,
  azure: 26,
  jobvite: 27,
  icims: 28,
  chartbuilder: 29,
  signify: 30,
  pointintime: 31,
  serverchart: 32,
  manualchart: 33,
  plan: 34,
  background: 35
};
export const enumIWizDataSet = {
  refresh: 0,
  interactive: 1,
  workingrefresh: 2,
  workinginteractive: 3
};
export const enumSaveScope = {
  entireChart: 0,
  currentBranch: 1,
  currentPage: 2,
  background: 3
};
export const kDefaultDirFields = [
  "Name",
  "Title",
  "PersonID",
  "Department",
  "Division",
  "Location",
  "Email",
  "Phone",
  "Mobile"
];

export const enumPubDest = { download: 0, folder: 1, nodownload: 2 };
export const enumHierarchyTypes = { employees: 0, positions: 1, grouping: 2 };
export const enumObjClassNames = {
  box: "Box",
  position: "Position",
  override: "Override"
} as const;

export const posObjClassFieldIds = {
  POSITION_TITLE: "Position Title",
  DESIRED_HOLDERS: "Desired Holders",
  HOLDER_COUNT: "HolderCount",
  PARENT_POSITION_ID: "Parent PositionID",
  POSITION_ID: "PositionID",
  POSITION_DIVISION: "Position Division",
  POSITION_LOCATION: "Position Location"
} as const;

export const boxObjClassFieldIds = {
  NAME: "Name",
  EMPLOYEE_ID: "EmployeeID"
} as const;

export var enumDataSourceFlag = { dsclear: 0, ultipro: 1 };
export var kMultiDocVersion = 2;
export const kLargeFramelessIcon = { imageWidth: 36, imageHeight: 36 };
export var kTabMgrWidth = 420;
export var kMinTabMgrWidth = 300;
export var kInitialPicPosX = 10;
export var kInitialPicPosY = 10;
export const kFormulaOptions = [
  "countif",
  "sumif",
  "averageif",
  "sum",
  "average",
  "concatenate",
  "lookup",
  "leftcalc",
  "rightcalc",
  "map",
  "math",
  "max",
  "min"
];
export const enumDocLoadType = {
  standard: 0,
  applymptemplate: 1,
  applymptemplate2: 2,
  compare: 3,
  applymptemplate_publish: 4,
  merge: 5
};
export const enumNewDocMode = {
  newDocLogin: 0,
  newDoc: 1,
  newDocImportWiz: 2,
  newPositionsDoc: 3,
  mockDocument: 4
};
export var kGridWidth = 575;
export var kGridHeight = 450;
export var enumImportStep = {
  loadDB: 0,
  loadBambooDB: 1,
  loadPeopleHRDB: 2,
  loadADPWorkforceNowDB: 3,
  loadSubscribeHRDB: 4,
  loadUltimateDB: 5,
  loadUltimateBIDB: 6,
  loadODBC: 7,
  loadSutiHRDB: 8,
  loadSalesForceDB: 9,
  loadBreatheHRDB: 10,
  loadOrangeDB: 11,
  loadSAPXMLDB: 12,
  loadSAPDB: 13,
  loadSFSFDB: 14,
  loadGoogleDB: 16,
  loadPaySpaceDB: 17,
  loadCustomDB: 18,
  loadNamelyDB: 19,
  loadWorkdayDB: 20,
  loadGreenHouseDB: 21,
  loadSignifyDB: 23,
  appenddata: 24,
  buildhier: 25,
  finishimport: 26,
  loadChartBuilderDB: 27
};
export var enumDocCreateStep = {
  loaddocmetadata: 0,
  loadchartdata: 1,
  applyoverrides: 2
};

export var gDoRefreshDiagnostic = false;
export var kTextWrapOptions = [
  "nowrap",
  "oneline",
  "twolines",
  "threelines",
  "fourlines",
  "unlimited"
];
export var kFPhotoSizeOptionsDDWidth = 80;
export var kPhotoSizeOptions = [
  "24 X 24",
  "32 X 32",
  "48 X 48",
  "72 X 72",
  "100 X 80",
  "80 X 100",
  "120 X 120",
  "150 X 150",
  "200 X 160",
  "160 X 200"
];
export var kFPhotoOptionsDDWidth = 120;
export var kPhotoMaskOptions = ["nomask", "cirmask", "grayscale"];
export const enumImageIndex = {
  appIcons: 0,
  enable16: 1,
  logo: 2,
  HTML5Icon: 3,
  logo2: 4,
  viewerIcons: 5,
  enable48drag: 6,
  premiumpaywall: 7,
  enableconnectors96: 8,
  enable256: 9,
  chartsettingsIcon: 10,
  accountsettingsIcon: 11,
  backgroundeditorIcon: 12,
  viewmanagerIcon: 13,
  apiStartIcon: 14,
  flatFileStartIcon: 15,
  manualStartIcon: 16,
  uploadFileIcon: 17,
  endmark: 18,
  endmarkinverted: 19,
  changeAdd: 20,
  changeComment: 21,
  changeDelete: 22,
  changeEdit: 23,
  changeLock: 24,
  changeMove: 25,
  changeMoveOut: 26,
  changeAddFlat: 27,
  changeCommentFlat: 28,
  changeDeleteFlat: 29,
  changeEditFlat: 30,
  changeMoveFlat: 31,
  changeMoveOutFlat: 32,
  changeGearFlat: 33,
  usersIcon: 34,
  userPlusIcon: 35,
  userMinusIcon: 36,
  // Position plan overlay icons
  positionAdd: 37,
  positionChange: 38,
  positionComment: 39,
  positionDelete: 40,
  positionMove: 41,
  positionUnassign: 42
};

export const enumImageNameIndex = {
  34: "users",
  36: "user-minus"
};

// Icon mapping for overlay icons based on document type and box type
export const overlayIconMapping = {
  employee: {
    add: enumImageIndex.changeAdd,
    change: enumImageIndex.changeEdit,
    comment: enumImageIndex.changeComment,
    delete: enumImageIndex.changeDelete,
    move: enumImageIndex.changeMove,
    moveout: enumImageIndex.changeMoveOut,
    lock: enumImageIndex.changeLock
  },
  position: {
    add: enumImageIndex.positionAdd,
    change: enumImageIndex.positionChange,
    comment: enumImageIndex.positionComment,
    delete: enumImageIndex.positionDelete,
    move: enumImageIndex.positionMove, // Both move and moveout use same icon in position plans
    moveout: enumImageIndex.positionMove, // Both move and moveout use same icon in position plans
    lock: enumImageIndex.changeLock, // Position plans use same lock icon as employee plans
    unassign: enumImageIndex.positionUnassign // New icon specific to position plans
  }
};
export var kCheckBoxSize = 12;
export var kCheckBoxMargin = 3;
export var kRadioButtonVertSpacing = 20;
export var kbuttonLabelHeight = 12;
export var enumPadBorderType = {
  fullBorder: 0,
  leftBorder: 1,
  middleBorder: 2,
  rightBorder: 3,
  tabBorder: 4,
  underline: 5,
  roundBorder: 6
};
export var enumButtonLabelPosition = { right: 0, below: 1, center: 2, left: 3 };
export var enumButtonIconPosition = { right: 0, left: 1 };
export const kCBTextContent = "TEXT";
export var kInternalOnlyCB = "[OCCONTENT]";
export var kCellInner = "#FFFFFF";
export var kCellOuter = "#A0A0A0";
export var kDisableColor = "#DCE6F4";
export var kHoverOuterColor = "#F29436";
export var kHoverInnerColor = "#FFE294";
export var kSelectOuterColor = "#EF4810";
export var kSelectInnerColor = "#FFE294";
export var kStartOuterColor = "#EF4810";
export const kPickerColors = [
  "#000000",
  "#7f7f7f",
  "#C3C3C3",
  "#D8D8D8",
  "#F2F2F2",
  "#FFFFFF",
  "#3F48CC",
  "#00A2E8",
  "#99D9EA",
  "#9CC3E5",
  "#BDD7EE",
  "#DEEBF6",
  "#B97A57",
  "#A34AA4",
  "#C8BFE7",
  "#7092BE",
  "#ADB9CA",
  "#D6DCE4",
  "#FFAEC9",
  "#22B14C",
  "#B5E61D",
  "#C3D69B",
  "#C5E0B3",
  "#E2EFD9",
  "#FFF200",
  "#FFC90E",
  "#FFD965",
  "#FFE389",
  "#FFF2CC",
  "#EFE4B0",
  "#880015",
  "#ED1C24",
  "#FF7F27",
  "#F4B183",
  "#F7CBAC",
  "#FBE5D5",
  "#FFFFFF",
  "#FFFFFF",
  "#FFFFFF",
  "#FFFFFF",
  "#FFFFFF",
  "#FFFFFF"
];
export const kPickerColorCodes = [
  "Black",
  "Gray50",
  "Gray25",
  "Gray15",
  "Gray5",
  "White",
  "Indigo",
  "Turquoise",
  "LightTurquoise",
  "BlueStrongAccent",
  "BlueAccent",
  "BlueAccentLight",
  "Brown",
  "Purple",
  "Lavender",
  "Bluegray",
  "BluegrayAccent",
  "BluegrayAccentLight",
  "Rose",
  "Green",
  "Lime",
  "GreenStrongAccent",
  "GreenAccent",
  "GreenAccentLight",
  "Yellow",
  "Gold",
  "GoldStrongAccent",
  "GoldAccent",
  "GoldLightAccent",
  "LightYellow",
  "DarkRed",
  "Red",
  "Orange",
  "OrangeStrongAccent",
  "OrangeAccent",
  "OrangeLightAccent",
  "Custom1",
  "Custom2",
  "Custom3",
  "Custom4",
  "Custom5",
  "Custom6"
];
export var kPickerBkgrnd = "";
export const kSmallCPHeight = 184;
export const kSmallCPHeightNoFill = 210;
export const kSmallCPButton = 24;
export const kTopOffset = 2;
export const kLeftOffset = 2;
export const kColRowSpacing = 2;
export const kNumCols = 6;
export const kNumRows = 7;
export var kCCPBkgrnd = "";
export var kCCPHeight = 100;
export var kAdvOptHeight = 210;
export var kAdvOptHeightLtd = 130;
export const enumColorPickAdvMode = { disabled: 0, full: 1, limited: 2 };
export const enumBrowsers = { IE: 0, Chrome: 1, FireFox: 2, Safari: 3, Other: 4 };
export const enumKeySource = { keyPress: 0, keyDown: 1 };
export const kShiftKey = 16;
export const kTabKey = 9;
export const kReturnKey = 13;
export const kEscKey = 27;
export const kBackspaceKey = 8;
export const kDeleteKey = 46;
export const kCtrlKey = 17;
export const kLeftArrow = 37;
export const kUpArrow = 38;
export const kRightArrow = 39;
export const kDownArrow = 40;
export const kF1Key = 112;
export const kF2Key = 113;
export const kF3Key = 114;
export const kF4Key = 115;
export const kF5Key = 116;
export const kF6Key = 117;
export const kF7Key = 118;
export const kF8Key = 119;
export const kF9Key = 120;
export const kF10Key = 121;
export const kF11Key = 122;
export const kF12Key = 123;
export const kPeriodKey = 190;
export const kPeriodCharCode = 46;
export const kSpaceCharCode = 32;
export const kPeriodKeyPad = 110;
export const kFramelessPanelSize = [72, 64];
export const kFramelessBorderThickness = 4;
export const kWebFrameMargin = 10;
export const kWebFrameHeader = 80;
export const kWebPanelMaxHeight = 450;
export const kFramelessDropDownWidth = 200;
export const kFramelessHeaderHeight = 26;
export const kFramelessPanelWidth = 300;
export const enumDragDropType = { none: 0, below: 1, above: 2 };
export const kDPIScreen = 96;
export const kCMI = 2.54;
export const gCurrentContextPromptSeriesID = 1;
export const kOCNTextColor = "#525D6B";
export const kLightPanelFillColor = "#f4f4f4";
export const kLightPanelSecondaryFillColor = "#e3e3e4";
export const kLightPanelAltFillColor = "#e0e0e0";
export const kLightPanelBorderColor = "#81817f";
export const kLightPanelBorderColorAlt = "#bfbfbf";
export const kLightPadColor = "#dedfe1";
export const kLightViewPortColor = "#e7e8ea";
export const kLightPanelTitleTextColor = "#313639";
export const kLightPanelMainTextColor = "#3d3d4a";
export const kLightPanelAltTextColor = kOCNTextColor;
export const kLightPanelTextSelectionColor = "#0D293E";
export const kLightPanelHoverTextColor = "#4B6EAF";
export const kLightPanelHighlightColor = "#ED8F03";
export const kLightPanelDisabledTextColor = "#7D7F81";
export const kLightPanelButtonColor = "#cdcfd3";
export const kLightPanelSecondaryButtonColor = "#F5F6F8";
export const kLightPanelBorderButtonColor = kLightPanelBorderColor;
export const kLightPanelFontIconColor = "#344563";
export const kLightPanelDisabledButtonColor = "#CED0D2";
export const kLightPanelTEFill = "#FFFFFF";
export const kLightPanelTEBorder = "#81817f";
export const kDarkPanelFillColor = "#222222";
export const kDarkPanelSecondaryFillColor = "#313335";
export const kDarkPanelAltFillColor = "#3C3F41";
export const kDarkPanelBorderColor = "#555555";
export const kDarkPadColor = "#2e2e2e";
export const kDarkViewPortColor = "#343434";
export const kDarkPanelTitleTextColor = "#E3E3E3";
export const kDarkPanelMainTextColor = "#BBBBBB";
export const kDarkPanelAltTextColor = kOCNTextColor;
export const kDarkPanelHoverTextColor = "#4B6EAF";
export const kDarkPanelTextSelectionColor = "#0D293E";
export const kDarkPanelHighlightColor = "#ED8F03";
export const kDarkPanelDisabledTextColor = "#ABABAB";
export const kDarkPanelButtonColor = "#3e3f41";
export const kDarkPanelSecondaryButtonColor = "#";
export const kDarkPanelBorderButtonColor = kDarkPanelBorderColor;
export const kDarkPanelDisabledButtonColor = "#636567";
export const kDarkPanelTEFill = "#383838";
export const kDarkPanelTEBorder = "#3C3F41";
export var enumPanelColors = {
  fill: {
    primary: kLightPanelFillColor,
    secondary: kLightPanelSecondaryFillColor,
    alt: kLightPanelAltFillColor
  },
  viewportcolor: kLightViewPortColor,
  border: kLightPanelBorderColor,
  lightborder: kLightPanelBorderColorAlt,
  button: {
    fill: kLightPanelButtonColor,
    secondary: kLightPanelSecondaryButtonColor,
    border: kLightPanelBorderButtonColor,
    disabled: kLightPanelDisabledButtonColor
  },
  textedit: { fill: kLightPanelTEFill, border: kLightPanelTEBorder },
  text: {
    header: kLightPanelTitleTextColor,
    body: kLightPanelMainTextColor,
    selection: kLightPanelTextSelectionColor,
    hover: kLightPanelHoverTextColor,
    highlight: kLightPanelHighlightColor,
    alt: kLightPanelAltTextColor,
    disabled: kLightPanelDisabledTextColor
  },
  fonticon: kLightPanelFontIconColor,
  padcolor: kLightPadColor
};
export var enumPanelDarkColors = {
  fill: {
    primary: kDarkPanelFillColor,
    secondary: kDarkPanelSecondaryFillColor,
    alt: kDarkPanelAltFillColor
  },
  viewportcolor: kDarkViewPortColor,
  border: kDarkPanelBorderColor,
  lightborder: kDarkPanelBorderColor,
  button: {
    fill: kDarkPanelButtonColor,
    border: kDarkPanelBorderButtonColor,
    disabled: kDarkPanelDisabledButtonColor
  },
  textedit: { fill: kDarkPanelTEFill, border: kDarkPanelTEBorder },
  text: {
    header: kDarkPanelTitleTextColor,
    body: kDarkPanelMainTextColor,
    selection: kDarkPanelTextSelectionColor,
    hover: kDarkPanelHoverTextColor,
    highlight: kDarkPanelHighlightColor,
    alt: kDarkPanelAltTextColor,
    disabled: kDarkPanelDisabledTextColor
  },
  fonticon: kDarkPanelTitleTextColor,
  padcolor: kDarkPadColor
};
export const kViewerTabColor = "#EEEEEE";
export const kOCNDarkGrayColor = "#444444";
export const kOCNLightGrayColor = "#9c9c97";
export const kOCNBlueGrayColor = "#a6b9d0";
export const kStdPickerBkgrnd = "#f2f2f2";
export const kLightPickerBkgrnd = "#fdfdfd";
export const kAlertOverlayColor = "#060606";
export var kRibLightGreyStart = "#f8f8f8";
export var kRibLightGreyEnd = "#ececec";
export var kRibLightGrey = "#fdfdfd";
export const kYellow = "#ffcc00";
export const kOrange = "#ED8F03";
export const kRed = "#F22222";
export const kGreen = "#00AA00";
export const kWhite = "#F4F0EC";
export const kBlack = "#222222";
export const kBlue = "#376ba6";
export const kCoral = "#f79537";
export const kSeaGreen = "#1a8d3c";
export const kChocolate = "#d65532";
export const kGrayCancel = "#4c4c4c";
export const kDarkRed = "#c32323";
export const kDarkYellow = "#e0b60f";
export const kDownloadButtonColor = "#0052CC";
export const kCreateButtonColor = kSeaGreen;
export const kPremiumPaywallHighlightColor = "#ffff07";
export const kDefaultNewHighlightColor = "#9CC3E5";
export const kUnicodeSymbols = [
  { name: "Check Mark", unicode: "\u2713" },
  { name: "Heavy Check Mark", unicode: "\u2714" },
  { name: "Multiplication X", unicode: "\u2716" },
  { name: "Ballot X", unicode: "\u2718" },
  { name: "Cross", unicode: "\u271A" },
  { name: "Heavy Check Mark", unicode: "\u2714" },
  { name: "Four Balloon Asterisk", unicode: "\u2723" },
  { name: "Heavy Four Balloon Asterisk", unicode: "\u2724" },
  { name: "Black Four Pointed Star", unicode: "\u2726" },
  { name: "White Four Pointed Star", unicode: "\u2727" },
  { name: "Circle White Star", unicode: "\u272A" },
  { name: "Stress Outlined White Star", unicode: "\u2729" },
  { name: "Arrowhead Right", unicode: "\u27A3" },
  { name: "Black Arrowhead Right", unicode: "\u27A4" },
  { name: "Sun", unicode: "\u2600" },
  { name: "Black Star", unicode: "\u2605" },
  { name: "White Star", unicode: "\u2606" },
  { name: "Cloud", unicode: "\u2601" },
  { name: "Black Telephone", unicode: "\u260E" },
  { name: "White Telephone", unicode: "\u260F" },
  { name: "White Smiley", unicode: "\u263A" },
  { name: "Black Smiley", unicode: "\u263B" },
  { name: "Bullet", unicode: "\u2022" },
  { name: "Grave Accent", unicode: "\u0060" },
  { name: "Horizontal Bar", unicode: "\u2015" },
  { name: "Horizontal Ellipsis", unicode: "\u2026" },
  { name: "Trade Mark Sign", unicode: "\u2122" },
  { name: "Service Mark", unicode: "\u2120" },
  { name: "Lozenge", unicode: "\u25CA" },
  { name: "Black Circle", unicode: "\u25CF" },
  { name: "White Circle", unicode: "\u25CB" },
  { name: "Dotted Circle", unicode: "\u25CC" },
  { name: "White Square", unicode: "\u25A1" },
  { name: "Black Small Square", unicode: "\u25AA" },
  { name: "White Small Square", unicode: "\u25AB" },
  { name: "White Small Circle", unicode: "\u25E6" },
  { name: "White Circle", unicode: "\u25CB" },
  { name: "Dotted Circle", unicode: "\u25CC" },
  { name: "Black Diamond", unicode: "\u25C6" },
  { name: "White Diamond", unicode: "\u25C6" },
  { name: "White Heart", unicode: "\u2661" },
  { name: "Black Heart", unicode: "\u2665" },
  { name: "White Spade", unicode: "\u2664" },
  { name: "Black Spade", unicode: "\u2663" },
  { name: "Black Flag", unicode: "\u2691" },
  { name: "Black Spade", unicode: "\u2690" },
  { name: "Lightning Bolt", unicode: "\u26A1" },
  { name: "No Entry", unicode: "\u26D4" },
  { name: "Envelope", unicode: "\u2709" },
  { name: "Airplane", unicode: "\u2708" },
  { name: "Pencil", unicode: "\u270E" },
  { name: "Check Mark", unicode: "\u2713" },

  { name: "Horizontal Bar", unicode: "\u2015" },
  { name: "Em Dash", unicode: "\u2014" },
  { name: "Wave Dash", unicode: "\u301C" },
  { name: "Wavy Dash", unicode: "\u3030" },
  { name: "Low Asterisk", unicode: "\u204E" },
  { name: "Two Asterisks Stacked", unicode: "\u2051" },
  { name: "Asterism", unicode: "\u2042" },
  { name: "Asterisk", unicode: "\u2217" },
  { name: "End of Proof", unicode: "\u220E" },
  { name: "Approximately Equal To", unicode: "\u2245" },

  { name: "Plus-Minus Sign", unicode: "\u00B1" },
  { name: "Left White Arrow", unicode: "\u21E6" },
  { name: "Up White Arrow", unicode: "\u21E7" },
  { name: "Right White Arrow", unicode: "\u21E8" },
  { name: "Down White Arrow", unicode: "\u21E9" },
  { name: "Up Down White Arrow", unicode: "\u21F3" },
  { name: "Increment", unicode: "\u2206" },
  { name: "Nabla", unicode: "\u2207" },
  { name: "Place of Interest", unicode: "\u2318" },
  { name: "Watch", unicode: "\u231A" },

  { name: "Hourglass", unicode: "\u231B" },
  { name: "Quadrant Lower Left and Upper Right", unicode: "\u2598" },
  { name: "Quadrant Lower Right", unicode: "\u2599" },
  { name: "Quadrant Upper Left and Lower Right", unicode: "\u259A" },
  { name: "Quadrant Upper Left", unicode: "\u259B" },
  { name: "Quadrant Upper Right", unicode: "\u259C" },
  { name: "Quadrant Lower Right", unicode: "\u259F" },
  { name: "Black Center Square", unicode: "\u25A0" },
  { name: "White Parallelogram", unicode: "\u25B1" },
  { name: "Black Parallelogram", unicode: "\u25B0" },

  { name: "Caduceus", unicode: "\u2624" },
  { name: "BioHazard", unicode: "\u2623" },
  { name: "Radioactive Sign", unicode: "\u2622" },
  { name: "Quadrant Upper Left and Lower Right", unicode: "\u259A" },
  { name: "Quadrant Upper Left", unicode: "\u259B" },
  { name: "Telephone Location", unicode: "\u2706" },
  { name: "Quadrant Lower Right", unicode: "\u259F" },
  { name: "Black Center Square", unicode: "\u25A0" },
  { name: "White Parallelogram", unicode: "\u25B1" },
  { name: "Black Parallelogram", unicode: "\u25B0" },

  { name: "Fire", unicode: "\uD83D" + "\uDD25" },
  { name: "Snowflake", unicode: "\u2744" }
];
export const kfontIconName = "Font Awesome 5 Pro";
export const kfontIconNameDuotone = "Font Awesome 5 Duotone";
export const kfontIconNameLightXSmall = '300 14px "Font Awesome 5 Pro"';
export const kfontIconNameLightSmall = '300 16px "Font Awesome 5 Pro"';
export const kfontIconNameLightLarge = '300 22px "Font Awesome 5 Pro"';
export const kfontIconNameLightMedium = '300 18px "Font Awesome 5 Pro"';
export const kfontIconNameLightXLarge = '300 25px "Font Awesome 5 Pro"';
export const kfontIconNameRegularXSmall = '400 14px "Font Awesome 5 Pro"';
export const kfontIconNameRegularSmall = '400 16px "Font Awesome 5 Pro"';
export const kfontIconNameRegularLarge = '400 22px "Font Awesome 5 Pro"';
export const kfontIconNameRegularMedium = '400 18px "Font Awesome 5 Pro"';
export const kfontIconNameRegularXLarge = '400 25px "Font Awesome 5 Pro"';
export const kfontIconNameRegularThicc = '400 40px "Font Awesome 5 Pro"';
export const kfontIconNameMini = '900 12px "Font Awesome 5 Pro"';
export const kfontIconNameXSmall = '900 14px "Font Awesome 5 Pro"';
export const kfontIconNameSmall = '900 16px "Font Awesome 5 Pro"';
export const kfontIconNameLarge = '900 22px "Font Awesome 5 Pro"';
export const kfontIconNameMedium = '900 18px "Font Awesome 5 Pro"';
export const kfontIconNameXLarge = '900 25px "Font Awesome 5 Pro"';
export const kfontIconNameHeavyThicc = '900 40px "Font Awesome 5 Pro"';
export const enumFontIconWeights = { light: "300", regular: "400", bold: "900" };
export const kFilterIcon = "\uf0b0";
export const kHighlightIcon = "\uf591";
export const kCheckedIcon = "\uf14a";
export const kUncheckedIcon = "\uf0c8";
export const kExitCircleChar = "\uf057";
export const kExitChar = "\uf00d";
export const kExpandChar = "\uf0b2";
export const kSyncIcon = "\uf021";
export const kPencilIcon = "\uf040";
export const kPencilIconAlt = "\uf303";
export const kAddIconCirlce = "\uf055";
export const kAddBranchIcon = "\uf830";
export const kToggleOnIcon = "\uf205";
export const kToggleOffIcon = "\uf204";
export const kAddIcon = "\uf067";
export const kMinusIcon = "\uf068";
export const kMinusCircleIcon = "\uf056";
export const kMinusSquareIcon = "\uf146";
export const kFormulaIcon = "\uf661";
export const kSigmaIcon = "\uf68b";
export const kPercentIcon = "\uf541";
export const kArrowUpIcon = "\uf062";
export const kLongArrowUp = "\uf176";
export const kLongArrowDown = "\uf175";
export const kTriangleDownIcon = "\u25BC";
export const kArrowRightIcon = "\uf061";
export const kLongArrowRightIcon = "\uf178";
export const kArrowLeftIcon = "\uf060";
export const kArrowDownIcon = "\uf063";
export const kArrowRightCircleIcon = "\uf0a9";
export const kArrowLeftCircleIcon = "\uf0a8";
export const kEditUser = "\uf4ff";
export const kChevronArrowDoubleUp = "\uf325";
export const kChevronArrowDoubleDown = "\uf322";
export const kDropDownButtonArrow = "\uf107";
export const kChevronRight = "\uf054";
export const kChevronLeft = "\uf053";
export const kChevronDown = "\uf078";
export const kChevronUp = "\uf077";
export const kDoubleChevronLeft = "\uf100";
export const kDoubleChevronRight = "\uf101";
export const kCircleCheckIcon = "\uf058";
export const kCircleXIcon = "\uf057";
export const kBreadCrumbIcon = "\uf7ec";
export const kBanIcon = "\uf05e";
export const kUnlockIcon = "\uf3c2";
export const kLockIcon = "\uf30d";
export const kCopyIcon = "\uf24d";
export const kUndoIcon = "\uf0e2";
export const kRedoIcon = "\uf01e";
export const kPasteIcon = "\uf328";
export const kCutIcon = "\uf0c4";
export const kDeleteSelectionIcon = "";
export const kTrashIcon = "\uf2ed";
export const kRecentIcon = "\uf1da";
export const kStarIcon = "\uf005";
export const kZoomIcon = "\uf00e";
export const kShareIcon = "\uf1e0";
export const kCautionIcon = "\uf071";
export const kCircleCautionIcon = "\uf06a";
export const kCheckIcon = "\uf00c";
export const kInfoCircle = "\uf05a";
export const kCommentIcon = "\uf27a";
export const kHelpIcon = "\uf128";
export const kPinChar = "\uf08d";
export const kBranchIcon = "\uf6ff";
export const kFields = "\uf036";
export const kWandIcon = "\uf0d0";
export const kSearchIcon = "\uf002";
export const kFileImportIcon = "\uf56f";
export const kFileExportIcon = "\uf56e";
export const kFileDownloadIcon = "\uf019";
export const kArrowDownload = "\uf34a";
export const kExportIcon = "\uf064";
export const kSaveIcon = "\uf0c7";
export const kNewDocIcon = "\uf319";
export const kCloseDocIcon = "\uf317";
export const kDocIcon = "\uf15b";
export const kDownloadFileIcon = "\uf34a";
export const kDownloadCloudIcon = "\uf381";
export const kCloudDownloadIcon = "\uf019";
export const kCloudUploadIcon = "\uf093";
export const kGearIcon = "\uf013";
export const kExpanderIcon = "\uf08e";
export const kRenameIcon = "\uf031";
export const kVertEllipsisIcon = "\uf142";
export const kEllipsisIcon = "\uf141";
export const kBars = "\uf0c9";
export const kHistoryIcon = "\uf1da";
export const kHome = "\uf015";
export const kClockIcon = "\uf017";
export const kCollapseIcon = "\uf101";
export const kExpandIcon = "\uf100";
export const kCircleIcon = "\uf111";
export const kGlobeIcon = "\uf0ac";
export const kSquareIcon = "\uf0c8";
export const kSquareCheckIcon = "\uf14a";
export const kDateIcon = "\uf073";
export const kCheckDisabled = "\uf0c8";
export const kCheckEnabled = "\uf14a";
export const kPhotoIcon = "\uf3e0";
export const kSearchPlusIcon = "\uf00e";
export const kSearchMinusIcon = "\uf010";
export const kNextIcon = "\uf054";
export const kUpIcon = "\uf077";
export const kDownIcon = "\uf078";
export const kCaretUp = "\uf0d8";
export const kCaretDown = "\uf0d7";
export const kCaretRight = "\uf0da";
export const kCaretLeft = "\uf0d9";
export const kUserSolid = "\uf03e";
export const kUserOutline = "\uf007";
export const kCurUser = "\uf4fc";
export const kAddUser = "\uf234";
export const kUserEdit = "\uf4ff";
export const kSecurityProfile = "\uf505";
export const kEye = "\uf06e";
export const kEyeSlash = "\uf070";
export const kSortIcon = "\uf15d";
export const kReverseSortIcon = "\uf15e";
export const kNotify = "\uf0f3";
export const kNotifySlash = "\uf1f6";
export const kUngroupIcon = "\uf248";
export const kSelectIcon = "\uf245";
export const kPanIcon = "\uf25a";
export const kAddSubIcon = "\uf234";
export const kAddAssistantIcon = "\uf830";
export const kCopyFormat = "\uf2d2";
export const kRemoveBreakIcon = "\uf5fe";
export const kAddBreakIcon = "\uf5ff";
export const kBranchStyleIcon = "\uf58d";
export const kLayoutEditorIcon = "\uf61c";
export const kAddFolderIcon = "\uf65e";
export const kMinusFolderIcon = "\uf65d";
export const kEditIcon = "\uf044";
export const kMoveIcon = "\uf047";
export const kTimesFolderIcon = "\uf65f";
export const kFolderIcon = "\uf07c";
export const kFolderClosedIcon = "\uf07b";
export const kAddFileIcon = "\uf319";
export const kCopyFileIcon = "\uf0c5";
export const kTimesFileIcon = "\uf317";
export const kRenameFileIcon = "\uf31c";
export const kPinIcon = "\uf08d";
export const kAutoTextIcon = "\uf0e7";
export const kResizeIcon = "\uf31d";
export const kTextIcon = "\uf893";
export const kTextTypeIcon = "\uf866";
export const kImageIcon = "\uf03e";
export const kTableIcon = "\uf84c";
export const kEnumTableIcon = "\uf0ce";
export const kPieChartIcon = "\uf200";
export const kLegendIcon = "\uf080";
export const kBrainIcon = "\uf808";
export const kEraserIcon = "\uf12d";
export const kExcelIcon = "\uf1c3";
export const kPDFIcon = "\uf1c1";
export const kRefreshIcon = kSyncIcon;
export const kPublishIcon = "\uf34a";
export const kSearchDirIcon = "\uf002";
export const kPointInTimeIcon = "\uf2f2";
export const kLinkIcon = "\uf0c1";
export const kEmailIcon = "\uf0e0";
export const kPhoneIcon = "\uf095";
export const kVideoChatIcon = "\uf03d";
export const kSendEmailIcon = "\uf1d8";
export const kMappingIcon = "\uf78a";
export const kFieldsFormulasIcon = "\uf661";
export const kPresentationIcon = "\uf685";
export const kProfileIcon = "\uf2c2";
export const kFilterHighlightIcon = "\uf0b0";
export const kChartBuilderIcon = "\uf1b3";
export const kSubchartShortcutIcon = "\uf02e";
export const kTreeIcon = "\uf802";
export const kGeneralIcon = kGearIcon;
export const kSecurityIcon = "\uf3ed";
export const kChartManagementIcon = "\uf0e8";
export const kThemeIcon = "\uf53f";
export const kAccessGroupsIcon = "\uf6a5";
export const kManageUsersIcon = "\uf500";
export const kAuthorizationIcon = "\uf084";
export const kUIProfilesIcon = "\uf5fd";
export const kCheckpointsIcon = "\uf3c5";
export const kLevelsIcon = "\uf5fd";
export const kBoxStyleIcon = "\uf2bb";
export const kBoxPropsIcon = "\uf853";
export const kGroupingIcon = "\uf247";
export const ellipsischar = "\u2026";
export const emdashchar = "\u2014";
export const nbsp = "\xa0";
export const kBulletChar = "\u2022";
export const kZeroWidthSpace = "\u200b";
export const kToggleOffIconDuo = "\uDBFC\uDE04";
export const kArrowRightIconDuo = "\uDBFC\uDC61";
export const kMetricsIcon = "\uf080";
export const kFieldsIcon = "\uf03a";
export const kBooksPlusIcon = "\uf7e8";
export const kFlaskGearIcon = "\ue5f1";
export var kCountries = [
  "United Kingdom",
  "France",
  "Italy",
  "South Africa",
  "Canada",
  "Mexico",
  "Saudi Arabia",
  "United Arab Emirates",
  "Poland",
  "Mexico",
  "Belgium",
  "Bulgaria",
  "Norway",
  "Australia",
  "New Zealand",
  "Germany",
  "Kuwait",
  "Malaysia",
  "Switzerland",
  "Dubai",
  "Ireland",
  "Israel",
  "Denmark",
  "Jamaica",
  "Argentina",
  "Spain",
  "Cyprus",
  "Japan",
  "China",
  "Hong Kong",
  "India",
  "Jordan",
  "Sierra Leone",
  "Singapore",
  "Chile"
].sort();
export var enumDividerDirection = { horzDivider: 0, vertDivider: 1 };
export var kUndoQueueMax = 10;
export const kSmallDDButtonHeight = 20;
export const kFramelessDropDownMarginH = 20;
export var kIDDWidth = 150;
export var kIDDSmallButtonHeight = 30;
export const kGridButtonW = 80;
export const kGridButtonH = 89;
export const kFooterOffset = 45;
export const enumElementDrawStates = { Std: 1, Hover: 2, MouseDown: 3, Disabled: 4 };
export const enumElementBorderStyle = {
  noBorder: 0,
  singleBorder: 1,
  doubleBorder: 2,
  roundBorder: 3,
  dashedBorder: 4
};
export var enumShortCutModKeys = { none: 0, ctrl: 1, shift: 2, ctrlshift: 3 };
export const enumKeyboardTargetEnterAction = {
  noAction: 0,
  selectAll: 1,
  insertAtEnd: 2
};
export var kTriangleHeightx = 5;
export var kbuttonImageBorder = 2;
export var kbuttonBorderThickness = 1;
export var kLargeIcon = { imageWidth: 48, imageHeight: 48 };
export var kMediumIcon = { imageWidth: 32, imageHeight: 32 };
export var kSmallIcon = { imageWidth: 24, imageHeight: 24 };
export var kExtraSmallIcon = { imageWidth: 16, imageHeight: 16 };
export var kTinyIcon = { imageWidth: 10, imageHeight: 10 };
export var kLargeWideIcon = { imageWidth: 72, imageHeight: 48 };
export var kXLargeIcon = { imageWidth: 84, imageHeight: 84 };
export var kXXLargeIcon = { imageWidth: 105, imageHeight: 105 };
export var kLargeWideButtonStandardWidth =
  kLargeWideIcon.imageWidth + 2 * kbuttonImageBorder + 2 * kbuttonBorderThickness;
export var kLargeWideButtonStandardHeight =
  kLargeWideIcon.imageHeight + 2 * kbuttonImageBorder + 2 * kbuttonBorderThickness;
export var kLargeButtonStandardSize =
  kLargeIcon.imageWidth + 2 * kbuttonImageBorder + 2 * kbuttonBorderThickness;
export var kSmallButtonStandardSize =
  kSmallIcon.imageWidth + 2 * kbuttonImageBorder + 2 * kbuttonBorderThickness;
export var kExtraSmallButtonStandardSize =
  kExtraSmallIcon.imageWidth + 2 * kbuttonImageBorder + 2 * kbuttonBorderThickness;
export var kTinyButtonStandardSize =
  kTinyIcon.imageWidth + 2 * kbuttonImageBorder + 2 * kbuttonBorderThickness;
export var enumRadioDirection = { horzRadio: 0, vertRadio: 1 };
export const enumFieldType = {
  text: 0,
  number: 1,
  image: 2,
  date: 3,
  object: 4,
  objectArray: 5,
  bool: 6
};

// Maps from enumFieldType keys/values to display labels
export const FieldNameToLabel: Record<string, string> = {
  text: "Text",
  number: "Number",
  image: "Image",
  date: "Date",
  object: "Object",
  objectArray: "Object Array",
  bool: "Boolean"
};

export const FieldTypeToLabel: Record<number, string> = {
  [enumFieldType.text]: "Text",
  [enumFieldType.number]: "Number",
  [enumFieldType.image]: "Image",
  [enumFieldType.date]: "Date",
  [enumFieldType.object]: "Object",
  [enumFieldType.objectArray]: "Object Array",
  [enumFieldType.bool]: "Boolean"
};

export const kOBJDBVersion = 2;
export const kReservedFieldNames = [
  "id",
  "locked",
  "persist",
  "level",
  "subchartlevel",
  "spanofcontrol",
  "subordinates",
  "headcount",
  "budgetedheadcount",
  "salaryrollup",
  "currentpositioncost",
  "positionbudgetrollup",
  "reportstoname",
  "newsuperid",
  "orphan",
  "topofsubchart",
  "peers",
  "scflag",
  "isaux",
  "alsoreportsto",
  "treeid",
  "allocationratio",
  "holdercount",
  "positioncount",
  "subordinatepositions",
  "directsubordinatepositions",
  "peerpositions"
];
export const kReservedFieldNamesFR = [
  "id",
  "verrouillé",
  "persister",
  "niveau page",
  "rapports directs",
  "subordonnés",
  "effectif",
  "reportea",
  "nouveauidresponsable",
  "orphelin",
  "sommetpage"
];
export const enumHyperLinkType = {
  notLink: 0,
  hyperlink: 1,
  email: 2,
  phone: 3,
  skype: 4,
  file: 5
};
export const enumCalcCode = {
  static: -1,
  level: 0,
  spanofcontrol: 1,
  subordinates: 2,
  headcount: 3,
  supervisorID: 4,
  subchartlevel: 5,
  ninebox: 6,
  customcalc: 7,
  newsupervisorID: 8,
  reportstoname: 9,
  peers: 10,
  alsoreportsto: 11,
  aggregatedField: 12,
  ratio: 13,
  holderCount: 14,
  positioncount: 15,
  subordinatepositions: 16,
  directsubordinatepositions: 17,
  peerpositions: 18
};
export var enumLockStatus = {
  notLocked: 0,
  contentLocked: 1,
  positionLocked: 2,
  contentPosLocked: 3
};
export var enumPersistStatus = { nopersist: 0, persist: 1 };
export var enumSortScope = { selection: 0, subchart: 1, chart: 2 };
export var kSortScopeName = ["sortselection", "sortsubchart", "sortchart"];
export const enumPanelBorderStyle = {
  noBorder: 0,
  singleBorder: 1,
  doubleBorder: 2,
  framelessBorder: 3,
  roundBorder: 4,
  dynamicClipBorder: 5
};
export const enumPanelMode = {
  arrowMode: 0,
  panMode: 1,
  marqueeMode: 2,
  otherMode: 3,
  combinedMode: 4,
  implicitMode: 5
};
export const enumPanelRenderMode = {
  all: 0,
  masterOnly: 1,
  contentOnly: 2,
  backgroundAndGhostChart: 3
};
export const enumElementLayer = { content: 0, master: 1 };
export const kMaxCanvasPanelSize = 16384;
export const kMaxCanvasDrawableSize = 30000;
export const kMaxPanelSize = 16384;

export const kPagingButtonMargin = 30;
export const enumBasicToolModes = {
  select: 0,
  marquee: 1,
  pan: 2,
  dragElem: 3,
  other: 4
};
export var enumMarqueeBehavior = {
  fullOverlap: 0,
  partialOverlap: 1,
  smartOverlap: 2
};
export var enumFillMode = {
  solidFill: 0,
  linearGradientDown: 1,
  noFill: 2,
  dashline: 3,
  triangle: 4,
  separator: 5,
  lefttriangle: 6,
  blank: 7,
  centertriangle: 8,
  opacityfill: 9,
  linearGradientAcross: 10
};
export const gBrightnessThreshold = 130;
export var kDoubleBorderWidthAdder = 3;
export var kRoundRectRadius = 8;
export const kStdTEHeight = 15;
export const kViewerTEHeight = 21;
export const kRuleComparisons = [
  "equals",
  "notequal",
  "startswith",
  "contains",
  "doesnotcontain",
  "isempty",
  "isnotempty",
  "greater",
  "less",
  "greaterequal",
  "lessequal",
  "inlist",
  "notinlist"
];
export const enumMatchSubs = { matchonly: 0, directonly: 1, allsubs: 2 };
export const enumMatchSubsLookup = { 0: "matchonly", 1: "directsubs", 2: "allsubs" };
export const ComparisonMapping = {
  equals: "equals",
  "not equal to": "notequal",
  "starts with": "startswith",
  contains: "contains",
  "is empty": "isempty",
  "is not empty": "isnotempty",
  "greater than": "greater",
  "less than": "less",
  "greater or equal to": "greaterequal",
  "less or equal to": "lessequal",
  "does not contain": "doesnotcontain",
  "in list": "inlist",
  "not in list": "notinlist"
};
export const enumFormulaScope = {
  selfsubs: 0,
  subs: 1,
  selfdirreports: 2,
  directreports: 3
};
export const kFormulaScopeList = ["selfsubs", "subsonly", "selfdirreports", "dirreports"];
export const enumFilterModes = { remove: 1, mute: 2, includefilteredmgrs: 3 };
export var NS_SVG = "http://www.w3.org/2000/svg";
export var XLINK_SVG = "http://www.w3.org/1999/xlink";
export var SVGDefaultMaskStrokeWidth = 3;
export const kTriangleOffset = kTriangleWidth / 2;
export const kTriangleHeight = 5;
export const enumSliderControl = { knob: 0, tickmark: 2 };
export const kTickSliderColor = "#333333";
export var enumSliderDirection = { pointsUp: 0, pointsDown: 1 };
export const kScrollBarThickness = 16;
export const kScrollBarButtonSize = 16;
export const enumScrollBarType = { horizontal: 0, vertical: 1 };
export var enumScrollBarButtonType = { left: 0, right: 1, up: 2, down: 3 };
export const kDefStaticHeight = 16;
export const enumStaticTextType = { singleline: 0, multiline: 1 };
export var kTabMargin = 8;
export var kTabButtonHeight = 26;
export const kListOptionHeight = 36;
export const actionButtonWidth = 26;
export const actionButtonHeight = 24;
export const actionPadding = 2;
export var kDropDownRightMargin = 5;
export const enumLanguage = { english: 0, french: 1, german: 2, spanish: 3 };
export const globalLanguageCodes = {
  en: "English",
  es: "Español",
  fr: "Français",
  de: "Deutsche",
  zo: "Zoomer"
};
export var enumTextEditFlashCursor = { drawCursor: 0, hideCursor: 1 };
export var enumAutoSizeBehavior = { cellEdit: 0, openEdit: 1 };
export var gTMDrawBuffer = null;
export var enumTextEditMove = { movingLeft: 0, movingRight: 1, notMoving: 2 };
export var kDefaultTimerInterval = 500;
export const kMinPassLength = 8;
export const kChineseFonts = ["SimSun"];
export const kDefaultBreakChars = escapeRegExp(" /-");
export var defaultDiacriticsRemovalap = [
  {
    base: "A",
    letters:
      "\u0041\u24B6\uFF21\u00C0\u00C1\u00C2\u1EA6\u1EA4\u1EAA\u1EA8\u00C3\u0100\u0102\u1EB0\u1EAE\u1EB4\u1EB2\u0226\u01E0\u00C4\u01DE\u1EA2\u00C5\u01FA\u01CD\u0200\u0202\u1EA0\u1EAC\u1EB6\u1E00\u0104\u023A\u2C6F"
  },
  { base: "AA", letters: "\uA732" },
  { base: "AE", letters: "\u00C6\u01FC\u01E2" },
  { base: "AO", letters: "\uA734" },
  { base: "AU", letters: "\uA736" },
  { base: "AV", letters: "\uA738\uA73A" },
  { base: "AY", letters: "\uA73C" },
  {
    base: "B",
    letters: "\u0042\u24B7\uFF22\u1E02\u1E04\u1E06\u0243\u0182\u0181"
  },
  {
    base: "C",
    letters: "\u0043\u24B8\uFF23\u0106\u0108\u010A\u010C\u00C7\u1E08\u0187\u023B\uA73E"
  },
  {
    base: "D",
    letters: "\u0044\u24B9\uFF24\u1E0A\u010E\u1E0C\u1E10\u1E12\u1E0E\u0110\u018B\u018A\u0189\uA779"
  },
  { base: "DZ", letters: "\u01F1\u01C4" },
  { base: "Dz", letters: "\u01F2\u01C5" },
  {
    base: "E",
    letters:
      "\u0045\u24BA\uFF25\u00C8\u00C9\u00CA\u1EC0\u1EBE\u1EC4\u1EC2\u1EBC\u0112\u1E14\u1E16\u0114\u0116\u00CB\u1EBA\u011A\u0204\u0206\u1EB8\u1EC6\u0228\u1E1C\u0118\u1E18\u1E1A\u0190\u018E"
  },
  { base: "F", letters: "\u0046\u24BB\uFF26\u1E1E\u0191\uA77B" },
  {
    base: "G",
    letters:
      "\u0047\u24BC\uFF27\u01F4\u011C\u1E20\u011E\u0120\u01E6\u0122\u01E4\u0193\uA7A0\uA77D\uA77E"
  },
  {
    base: "H",
    letters: "\u0048\u24BD\uFF28\u0124\u1E22\u1E26\u021E\u1E24\u1E28\u1E2A\u0126\u2C67\u2C75\uA78D"
  },
  {
    base: "I",
    letters:
      "\u0049\u24BE\uFF29\u00CC\u00CD\u00CE\u0128\u012A\u012C\u0130\u00CF\u1E2E\u1EC8\u01CF\u0208\u020A\u1ECA\u012E\u1E2C\u0197"
  },
  { base: "J", letters: "\u004A\u24BF\uFF2A\u0134\u0248" },
  {
    base: "K",
    letters: "\u004B\u24C0\uFF2B\u1E30\u01E8\u1E32\u0136\u1E34\u0198\u2C69\uA740\uA742\uA744\uA7A2"
  },
  {
    base: "L",
    letters:
      "\u004C\u24C1\uFF2C\u013F\u0139\u013D\u1E36\u1E38\u013B\u1E3C\u1E3A\u0141\u023D\u2C62\u2C60\uA748\uA746\uA780"
  },
  { base: "LJ", letters: "\u01C7" },
  { base: "Lj", letters: "\u01C8" },
  { base: "M", letters: "\u004D\u24C2\uFF2D\u1E3E\u1E40\u1E42\u2C6E\u019C" },
  {
    base: "N",
    letters:
      "\u004E\u24C3\uFF2E\u01F8\u0143\u00D1\u1E44\u0147\u1E46\u0145\u1E4A\u1E48\u0220\u019D\uA790\uA7A4"
  },
  { base: "NJ", letters: "\u01CA" },
  { base: "Nj", letters: "\u01CB" },
  {
    base: "O",
    letters:
      "\u004F\u24C4\uFF2F\u00D2\u00D3\u00D4\u1ED2\u1ED0\u1ED6\u1ED4\u00D5\u1E4C\u022C\u1E4E\u014C\u1E50\u1E52\u014E\u022E\u0230\u00D6\u022A\u1ECE\u0150\u01D1\u020C\u020E\u01A0\u1EDC\u1EDA\u1EE0\u1EDE\u1EE2\u1ECC\u1ED8\u01EA\u01EC\u00D8\u01FE\u0186\u019F\uA74A\uA74C"
  },
  { base: "OI", letters: "\u01A2" },
  { base: "OO", letters: "\uA74E" },
  { base: "OU", letters: "\u0222" },
  { base: "OE", letters: "\u008C\u0152" },
  { base: "oe", letters: "\u009C\u0153" },
  {
    base: "P",
    letters: "\u0050\u24C5\uFF30\u1E54\u1E56\u01A4\u2C63\uA750\uA752\uA754"
  },
  { base: "Q", letters: "\u0051\u24C6\uFF31\uA756\uA758\u024A" },
  {
    base: "R",
    letters:
      "\u0052\u24C7\uFF32\u0154\u1E58\u0158\u0210\u0212\u1E5A\u1E5C\u0156\u1E5E\u024C\u2C64\uA75A\uA7A6\uA782"
  },
  {
    base: "S",
    letters:
      "\u0053\u24C8\uFF33\u1E9E\u015A\u1E64\u015C\u1E60\u0160\u1E66\u1E62\u1E68\u0218\u015E\u2C7E\uA7A8\uA784"
  },
  {
    base: "T",
    letters:
      "\u0054\u24C9\uFF34\u1E6A\u0164\u1E6C\u021A\u0162\u1E70\u1E6E\u0166\u01AC\u01AE\u023E\uA786"
  },
  { base: "TZ", letters: "\uA728" },
  {
    base: "U",
    letters:
      "\u0055\u24CA\uFF35\u00D9\u00DA\u00DB\u0168\u1E78\u016A\u1E7A\u016C\u00DC\u01DB\u01D7\u01D5\u01D9\u1EE6\u016E\u0170\u01D3\u0214\u0216\u01AF\u1EEA\u1EE8\u1EEE\u1EEC\u1EF0\u1EE4\u1E72\u0172\u1E76\u1E74\u0244"
  },
  { base: "V", letters: "\u0056\u24CB\uFF36\u1E7C\u1E7E\u01B2\uA75E\u0245" },
  { base: "VY", letters: "\uA760" },
  {
    base: "W",
    letters: "\u0057\u24CC\uFF37\u1E80\u1E82\u0174\u1E86\u1E84\u1E88\u2C72"
  },
  { base: "X", letters: "\u0058\u24CD\uFF38\u1E8A\u1E8C" },
  {
    base: "Y",
    letters:
      "\u0059\u24CE\uFF39\u1EF2\u00DD\u0176\u1EF8\u0232\u1E8E\u0178\u1EF6\u1EF4\u01B3\u024E\u1EFE"
  },
  {
    base: "Z",
    letters: "\u005A\u24CF\uFF3A\u0179\u1E90\u017B\u017D\u1E92\u1E94\u01B5\u0224\u2C7F\u2C6B\uA762"
  },
  {
    base: "a",
    letters:
      "\u00AA\u0061\u24D0\uFF41\u1E9A\u00E0\u00E1\u00E2\u1EA7\u1EA5\u1EAB\u1EA9\u00E3\u0101\u0103\u1EB1\u1EAF\u1EB5\u1EB3\u0227\u01E1\u00E4\u01DF\u1EA3\u00E5\u01FB\u01CE\u0201\u0203\u1EA1\u1EAD\u1EB7\u1E01\u0105\u2C65\u0250"
  },
  { base: "aa", letters: "\uA733" },
  { base: "ae", letters: "\u00E6\u01FD\u01E3" },
  { base: "ao", letters: "\uA735" },
  { base: "au", letters: "\uA737" },
  { base: "av", letters: "\uA739\uA73B" },
  { base: "ay", letters: "\uA73D" },
  {
    base: "b",
    letters: "\u0062\u24D1\uFF42\u1E03\u1E05\u1E07\u0180\u0183\u0253"
  },
  {
    base: "c",
    letters: "\u0063\u24D2\uFF43\u0107\u0109\u010B\u010D\u00E7\u1E09\u0188\u023C\uA73F\u2184"
  },
  {
    base: "d",
    letters: "\u0064\u24D3\uFF44\u1E0B\u010F\u1E0D\u1E11\u1E13\u1E0F\u0111\u018C\u0256\u0257\uA77A"
  },
  { base: "dz", letters: "\u01F3\u01C6" },
  {
    base: "e",
    letters:
      "\u0065\u24D4\uFF45\u00E8\u00E9\u00EA\u1EC1\u1EBF\u1EC5\u1EC3\u1EBD\u0113\u1E15\u1E17\u0115\u0117\u00EB\u1EBB\u011B\u0205\u0207\u1EB9\u1EC7\u0229\u1E1D\u0119\u1E19\u1E1B\u0247\u025B\u01DD"
  },
  { base: "f", letters: "\u0066\u24D5\uFF46\u1E1F\u0192\uA77C" },
  {
    base: "g",
    letters:
      "\u0067\u24D6\uFF47\u01F5\u011D\u1E21\u011F\u0121\u01E7\u0123\u01E5\u0260\uA7A1\u1D79\uA77F"
  },
  {
    base: "h",
    letters:
      "\u0068\u24D7\uFF48\u0125\u1E23\u1E27\u021F\u1E25\u1E29\u1E2B\u1E96\u0127\u2C68\u2C76\u0265"
  },
  { base: "hv", letters: "\u0195" },
  {
    base: "i",
    letters:
      "\u0069\u24D8\uFF49\u00EC\u00ED\u00EE\u0129\u012B\u012D\u00EF\u1E2F\u1EC9\u01D0\u0209\u020B\u1ECB\u012F\u1E2D\u0268\u0131"
  },
  { base: "j", letters: "\u006A\u24D9\uFF4A\u0135\u01F0\u0249" },
  {
    base: "k",
    letters: "\u006B\u24DA\uFF4B\u1E31\u01E9\u1E33\u0137\u1E35\u0199\u2C6A\uA741\uA743\uA745\uA7A3"
  },
  {
    base: "l",
    letters:
      "\u006C\u24DB\uFF4C\u0140\u013A\u013E\u1E37\u1E39\u013C\u1E3D\u1E3B\u017F\u0142\u019A\u026B\u2C61\uA749\uA781\uA747"
  },
  { base: "lj", letters: "\u01C9" },
  { base: "m", letters: "\u006D\u24DC\uFF4D\u1E3F\u1E41\u1E43\u0271\u026F" },
  {
    base: "n",
    letters:
      "\u006E\u24DD\uFF4E\u01F9\u0144\u00F1\u1E45\u0148\u1E47\u0146\u1E4B\u1E49\u019E\u0272\u0149\uA791\uA7A5"
  },
  { base: "nj", letters: "\u01CC" },
  {
    base: "o",
    letters:
      "\u006F\u24DE\uFF4F\u00F2\u00F3\u00F4\u1ED3\u1ED1\u1ED7\u1ED5\u00F5\u1E4D\u022D\u1E4F\u014D\u1E51\u1E53\u014F\u022F\u0231\u00F6\u022B\u1ECF\u0151\u01D2\u020D\u020F\u01A1\u1EDD\u1EDB\u1EE1\u1EDF\u1EE3\u1ECD\u1ED9\u01EB\u01ED\u00F8\u01FF\u0254\uA74B\uA74D\u0275"
  },
  { base: "oi", letters: "\u01A3" },
  { base: "ou", letters: "\u0223" },
  { base: "oo", letters: "\uA74F" },
  {
    base: "p",
    letters: "\u0070\u24DF\uFF50\u1E55\u1E57\u01A5\u1D7D\uA751\uA753\uA755"
  },
  { base: "q", letters: "\u0071\u24E0\uFF51\u024B\uA757\uA759" },
  {
    base: "r",
    letters:
      "\u0072\u24E1\uFF52\u0155\u1E59\u0159\u0211\u0213\u1E5B\u1E5D\u0157\u1E5F\u024D\u027D\uA75B\uA7A7\uA783"
  },
  {
    base: "s",
    letters:
      "\u0073\u24E2\uFF53\u00DF\u015B\u1E65\u015D\u1E61\u0161\u1E67\u1E63\u1E69\u0219\u015F\u023F\uA7A9\uA785\u1E9B"
  },
  {
    base: "t",
    letters:
      "\u0074\u24E3\uFF54\u1E6B\u1E97\u0165\u1E6D\u021B\u0163\u1E71\u1E6F\u0167\u01AD\u0288\u2C66\uA787"
  },
  { base: "tz", letters: "\uA729" },
  {
    base: "u",
    letters:
      "\u0075\u24E4\uFF55\u00F9\u00FA\u00FB\u0169\u1E79\u016B\u1E7B\u016D\u00FC\u01DC\u01D8\u01D6\u01DA\u1EE7\u016F\u0171\u01D4\u0215\u0217\u01B0\u1EEB\u1EE9\u1EEF\u1EED\u1EF1\u1EE5\u1E73\u0173\u1E77\u1E75\u0289"
  },
  { base: "v", letters: "\u0076\u24E5\uFF56\u1E7D\u1E7F\u028B\uA75F\u028C" },
  { base: "vy", letters: "\uA761" },
  {
    base: "w",
    letters: "\u0077\u24E6\uFF57\u1E81\u1E83\u0175\u1E87\u1E85\u1E98\u1E89\u2C73"
  },
  { base: "x", letters: "\u0078\u24E7\uFF58\u1E8B\u1E8D" },
  {
    base: "y",
    letters:
      "\u0079\u24E8\uFF59\u1EF3\u00FD\u0177\u1EF9\u0233\u1E8F\u00FF\u1EF7\u1E99\u1EF5\u01B4\u024F\u1EFF"
  },
  {
    base: "z",
    letters: "\u007A\u24E9\uFF5A\u017A\u1E91\u017C\u017E\u1E93\u1E95\u01B6\u0225\u0240\u2C6C\uA763"
  }
];
export var diacriticsMap = {};
export const enumWSInteractMode = {
  standardMode: 0,
  panMode: 1,
  marqueeMode: 2,
  controlMode: 3,
  elementDragMode: 4
};
export var enumXMLType = { PDF: 0, PPT: 1 };
export var enumXMLLoaderErr = { noErr: 0, incompleteLine: 1 };
export var enumTagState = { unknown: 0, foundOpen: 1 };
export const enumObjectTypes = {
  position: "Position",
  holder: "Holder",
  person: "Person",
  functionalLabel: "Function"
};

export const objTypeClassMap = {
  [enumObjectTypes.position]: enumObjClassNames.position,
  [enumObjectTypes.holder]: enumObjClassNames.box
} as const;

export const kConnectorRefreshDisabled = ["Paycom", "Cornerstone", "ADP Detail"];

export const enumFeaturesList = {
  languageSwitcher: "languageSwitcher",
  mouseControls: "mouseControls",
  measurements: "measurements",
  theme: "theme",
  userDetails: "userDetails",
  chartExplorer: "chartExplorer",
  basicSearch: "basicSearch",
  advancedSearch: "advancedSearch",
  interactiveFieldToggle: "interactiveFieldToggle",
  levelsDown: "levelsDown",
  levelsUp: "levelsUp",
  breadcrumbs: "breadcrumbs",
  refresh: "refresh",
  profile: "profile",
  profileFieldStarring: "profileFieldStarring",
  subcharts: "subcharts",
  viewSwitcher: "viewSwitcher",
  savedFilters: "savedFilters",
  interactiveFilters: "interactiveFilters",
  savedHighlights: "savedHighlights",
  interactiveHighlights: "interactiveHighlights",
  editBackgrounds: "editBackgrounds",
  switchBackgrounds: "switchBackgrounds",
  editView: "editView",
  customChart: "customChart",
  trackChanges: "trackChanges",
  staging: "staging",
  manualChanges: "manualChanges",
  autoSave: "autoSave",
  autoSync: "autoSync",
  chartRules: "chartRules",
  boxStyles: "boxStyles",
  grouping: "grouping",
  appendSources: "appendSources",
  mergeSources: "mergeSources",
  appendTables: "appendTables",
  coHeads: "coHeads",
  functionalChart: "functionalChart",
  auxMapping: "auxMapping",
  promoteViews: "promoteViews",
  invertedChart: "invertedChart",
  baseChartSort: "baseChartSort",
  adminProfileFieldStarring: "adminProfileFieldStarring",
  staticFormulas: "staticFormulas",
  dynamicFormulas: "dynamicFormulas",
  listFields: "listFields",
  pickLists: "pickLists",
  adminExcelFields: "adminExcelFields",
  importFilters: "importFilters",
  subchartNaming: "subchartNaming",
  subchartSorting: "subchartSorting",
  autoSubchartBreaks: "autoSubchartBreaks",
  exportPDFPPT: "exportPDFPPT",
  exportExcel: "exportExcel",
  exportDisconnected: "exportDisconnected",
  exportPNG: "exportPNG",
  exportWeblink: "exportWeblink",
  autoExport: "autoExport",
  defaultLaunchMode: "defaultLaunchMode",
  activityReport: "activityReport",
  adminLog: "adminLog",
  dateTimeFormat: "dateTimeFormat",
  subchartArrowStyle: "subchartArrowStyle",
  accessGroups: "accessGroups",
  addUsers: "addUsers",
  uiProfiles: "uiProfiles",
  SSOAutoPro: "SSOAutoPro",
  SAMLGroupMapping: "SAMLGroupMapping",
  groupAutoAssignment: "groupAutoAssignment",
  SLO: "SLO",
  signoutRedirectURL: "signoutRedirectURL",
  checkpoints: "checkpoints",
  MFA: "MFA",
  reporting: "reporting",
  PIT: "PIT",
  PITAutoArchive: "PITAutoArchive",
  PITDeltaReports: "PITDeltaReports",
  PITArchiveCleanup: "PITArchiveCleanup",
  planning: "planning",
  planMetrics: "planMetrics",
  planChanges: "planChanges",
  talentPool: "talentPool",
  adminUserImpersonation: "adminUserImpersonation",
  RLS: "RLS",
  BLS: "BLS",
  dataConnectorLimit: "dataConnectorLimit",
  hasAdvSec: "hasAdvSec",
  importFilterLimit: "importFilterLimit",
  positionManagement: "positionManagement",
  levelChart: "levelChart",
  directory: "directory",
  accessGroupLimit: "accessGroupLimit",
  formulasLimit: "formulasLimit",
  conditionalRLS: "conditionalRLS",
  planApprovals: "planApprovals",
  SCIM: "SCIM"
} as const;

export const AssignmentStatus = {
  Assigned: "Assigned",
  Unassigned: "Unassigned",
  Released: "Released",
  AssignedOutOfScope: "Assigned Out Of Scope"
} as const;

export const PositionStatus = {
  Active: "Active",
  Closed: "Closed"
} as const;

export const connectorsBuiltDynamically = ["OPP"];
export const kMaxSCNodesPerDBObj = 2;

export const DISPLAY_MODES = {
  CHART: "chart",
  TABLE: "table",
  DIRECTORY: "directory",
  VIEWMANAGER: "viewmanager"
} as const;

export const SEARCH_PARAM_DISPLAY_KEY = "display";

// Add more actions as needed
export const BOX_ACTIONS = {
  REOPEN: "reopen"
} as const;

export type TBoxActions = (typeof BOX_ACTIONS)[keyof typeof BOX_ACTIONS];

export const kBlockedFieldTag = "__blocked__";

export const kReservedMetricsFields = [
  kHeadcount,
  kBudgetedHeadcount,
  kSalaryRollup,
  kCurrentPositionCost,
  kPositionBudgetRollup,
  kAllocation,
  kPositionCount,
  kSubordinatePositions,
  kDirectSubordinatePositions,
  kPeerPositions
];

// Fields excluded from being removed with Import Filters due to being commonly used in formulas or as part of the organizational structure
export const kExcludedLimitFieldsEmployeeCharts = {
  [enumObjectTypes.holder]: [
    "SubChart Level",
    kSpanOfControl,
    "Subordinates",
    kHeadcount,
    kObjtype,
    kPeersID,
    kLevelID,
    "treeID"
  ],
  [enumObjectTypes.position]: []
};

export const kExcludedLimitFieldsPositionCharts = {
  [enumObjectTypes.position]: [
    kAllocation,
    kBudgetedHeadcount,
    "Disconnect",
    kHeadcount,
    kLevelID,
    kPeersID,
    kSpanOfControl,
    "Subordinates",
    kPositionAllocation,
    kObjtype,
    "treeID",
    "Assignment Status",
    kPositionCostBandMaximum,
    kPositionCostBandMidpoint,
    kPositionCostBandMinimum,
    kPositionBudgetedSalary,
    kPositionBudgetRollup,
    "_objInfo",
    kStaffingTarget,
    kSalaryRollup,
    kCurrentPositionCost,
    "SubChart Level",
    kHolderCount
  ],
  [enumObjectTypes.holder]: [
    ...kExcludedLimitFieldsEmployeeCharts[enumObjectTypes.holder],
    kAssignmentStatus,
    kEmployeeCostCenter,
    kEmployeePay,
    kEmployeeSalary,
    "_objInfo"
  ]
};

export const languageNames = {
  [enumLanguage.english]: "English",
  [enumLanguage.french]: "French",
  [enumLanguage.german]: "German",
  [enumLanguage.spanish]: "Spanish"
};

export const enumUpdatePlanModes = {
  duplicateplan: "duplicateplan",
  saveplan: "saveplan",
  deleteplan: "deleteplan",
  listPlanSubmissions: "listPlanSubmissions",
  updateplanusers: "updateplanusers"
};

export const kEnumToLangCode = { 0: "en", 1: "fr", 2: "de", 3: "es" };

export const kNIHFunctionalStatementsFieldID = "FUNCTIONAL STATEMENTS";
